import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { DEFAULT_ANALYTICS_REPORTS } from '../config/analyticsReports';
import PolicyDeviceCards from './PolicyDeviceCards';
import BaselineDeviceCards from './BaselineDeviceCards';
import PlatformComparisonDashboard from './PlatformComparisonDashboard';
import ApplicationDeployment from './ApplicationDeployment';
import CustomReports from './CustomReports';
import DefaultReportView from './DefaultReportView';
import PolicyBuilderModal from './PolicyBuilderModal';
import DeploymentModal from './DeploymentModal';
import DevicePicker from './DevicePicker';
import { classifyCommandOutcome } from '../utils/commandOutcome';
import Settings from './Settings';
import AgentSettings from './AgentSettings';
import GoAgentManagement from './GoAgentManagement';
import LicenseManagement from './LicenseManagement';
import PatchManagement from './PatchManagement';
import LanguageSelector from './LanguageSelector';
import HealthHoneycombView from './HealthHoneycombView';
import { useAuth } from '../contexts/AuthContext';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';
import {
  loadDirectory,
  saveDirectory,
  loadPolicyLibrary,
  savePolicyLibrary,
} from '../services/directoryStore';
import {
  LayoutDashboard,
  Server,
  Folder,
  ChevronRight,
  ChevronDown,
  Terminal,
  Play,
  Search,
  Activity,
  Shield,
  ShieldCheck,
  Settings as SettingsIcon,
  CheckCircle,
  AlertCircle,
  MoreVertical,
  X,
  Layers,
  Filter,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  Box,
  Briefcase,
  FileText,
  Zap,
  CheckSquare,
  Target,
  Cpu,
  WifiOff,
  Globe,
  Apple,
  Users,
  Building2,
  Plus,
  Trash2,
  Edit,
  Move,
  FolderPlus,
  Upload,
  History,
  Download,
  FileSpreadsheet,
  AlertTriangle,
  Flag,
  LogOut
} from 'lucide-react';

// --- Device Analytics Groups (default reports from shared config) ---
const analyticsGroups = [
  {
    id: 'analytics-default',
    name: 'Default Reports',
    type: 'analytics-group',
    category: 'analytics',
    description: 'Pre-built reports for common device analytics and insights',
    children: DEFAULT_ANALYTICS_REPORTS.map((r) => ({ id: r.id, name: r.name, type: 'report', category: 'analytics' }))
  },
  {
    id: 'analytics-custom',
    name: 'Custom Reports',
    type: 'analytics-group',
    category: 'analytics',
    description: 'Create and manage custom device analytics reports',
    children: [
      { id: 'custom-reports', name: 'Manage Custom Reports', type: 'custom-reports', category: 'analytics' }
    ]
  }
];

// --- Baselines (Templates) ---
const baselines = [
  {
    id: 'base-std',
    name: 'Standard Baseline',
    type: 'baseline-group',
    category: 'baseline',
    description: 'Core configurations applied to all workstations. Includes mandatory security policies, user account management, and patch management protocols to ensure a secure and consistent baseline environment.',
    children: [
      { id: 'tmpl-policies', name: 'Policies', type: 'template', category: 'baseline' },
      { id: 'tmpl-patch', name: 'Patch Management', type: 'template', category: 'baseline' }
    ]
  },
  {
    id: 'base-adv',
    name: 'Advanced Baselines',
    type: 'baseline-group',
    category: 'baseline',
    description: 'Specialized configurations for specific user roles or high-performance environments. Includes developer setups, server hardening profiles, and advanced system tuning parameters.',
    children: [
      { id: 'tmpl-dev-env', name: 'Developer Env Setup', type: 'template', category: 'baseline' },
      { id: 'tmpl-server-hard', name: 'Server Hardening', type: 'template', category: 'baseline' }
    ]
  },
  {
    id: 'base-sec',
    name: 'Security & Compliance',
    type: 'baseline-group',
    category: 'baseline',
    description: 'Stringent security controls for high-risk assets. Enforces industry standards (CIS Level 1), strict device control, and comprehensive audit logging for compliance requirements.',
    children: [
      { id: 'tmpl-cis-lvl1', name: 'CIS Level 1 Benchmark', type: 'template', category: 'baseline' },
      { id: 'tmpl-audit', name: 'Full System Audit', type: 'template', category: 'baseline' }
    ]
  }
];

// --- Scripts ---
const allScripts = [
  // Policies (Everything except Patch Mgmt)
  { 
    id: 'scr-guest-disable', 
    title: 'Disable Guest Account', 
    description: 'Disables the built-in Guest account to prevent unauthorized access.', 
    lang: 'PowerShell', 
    icon: Users, 
    color: 'text-orange-500', 
    risk: 'Low', 
    category: 'cat-policies',
    content: `Disable-LocalUser -Name "Guest"`
  },
  { 
    id: 'scr-firewall-on', 
    title: 'Enable Windows Firewall', 
    description: 'Enables Windows Firewall for Domain, Public, and Private profiles.', 
    lang: 'PowerShell', 
    icon: Shield, 
    color: 'text-green-500', 
    risk: 'Low', 
    category: 'cat-policies',
    content: `Set-NetFirewallProfile -Profile Domain,Public,Private -Enabled True`
  },
  { 
    id: 'scr-win-store-off', 
    title: 'Disable Windows Store', 
    description: 'Prevents users from installing unapproved apps/bloatware.', 
    lang: 'PowerShell', 
    icon: Box, 
    color: 'text-red-500', 
    risk: 'High', 
    category: 'cat-policies',
    content: `
      $path = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\WindowsStore"
      if (!(Test-Path $path)) { New-Item -Path $path -Force }
      Set-ItemProperty -Path $path -Name "RemoveWindowsStore" -Value 1
    `
  },

  // Patch Management Scripts
  { 
    id: 'scr-patch-config', 
    title: 'Patch Control & Reboot Logic', 
    description: 'Configures Windows Update deferrals, active hours, and reboot behavior.', 
    lang: 'PowerShell', 
    icon: Activity, 
    color: 'text-blue-500', 
    risk: 'Medium', 
    category: 'cat-patch',
    content: `
      param(
        [int]$DeferQualityUpdatesPeriodinDays = 15,
        [int]$DeferFeatureUpdatesPeriodinDays = 180,
        [int]$ActiveHoursStart = 8,
        [int]$ActiveHoursEnd = 18,
        [bool]$NoAutoRebootWithLoggedOnUsers = $true
      )
      $wuPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\WindowsUpdate"
      if (-not (Test-Path $wuPath)) { New-Item -Path $wuPath -Force | Out-Null }
      Set-ItemProperty -Path $wuPath -Name "DeferQualityUpdates" -Value 1 -Type DWord
      Set-ItemProperty -Path $wuPath -Name "DeferQualityUpdatesPeriodinDays" -Value $DeferQualityUpdatesPeriodinDays -Type DWord
      Set-ItemProperty -Path $wuPath -Name "DeferFeatureUpdates" -Value 1 -Type DWord
      Set-ItemProperty -Path $wuPath -Name "DeferFeatureUpdatesPeriodinDays" -Value $DeferFeatureUpdatesPeriodinDays -Type DWord
      Set-ItemProperty -Path $wuPath -Name "SetActiveHours" -Value 1 -Type DWord
      Set-ItemProperty -Path $wuPath -Name "ActiveHoursStart" -Value $ActiveHoursStart -Type DWord
      Set-ItemProperty -Path $wuPath -Name "ActiveHoursEnd" -Value $ActiveHoursEnd -Type DWord
      $auPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\WindowsUpdate\\AU"
      if (-not (Test-Path $auPath)) { New-Item -Path $auPath -Force | Out-Null }
      Set-ItemProperty -Path $auPath -Name "AUOptions" -Value 4 -Type DWord
      Set-ItemProperty -Path $auPath -Name "NoAutoRebootWithLoggedOnUsers" -Value ($NoAutoRebootWithLoggedOnUsers ? 1 : 0) -Type DWord
      Restart-Service wuauserv -Force
    `,
    params: [
      { name: 'DeferQualityUpdatesPeriodinDays', label: 'Defer Quality Updates (Days)', type: 'number', placeholder: '15', default: '15', required: true },
      { name: 'DeferFeatureUpdatesPeriodinDays', label: 'Defer Feature Updates (Days)', type: 'number', placeholder: '180', default: '180', required: true },
      { name: 'ActiveHoursStart', label: 'Active Hours Start (0-23)', type: 'number', placeholder: '8', default: '8', required: true },
      { name: 'ActiveHoursEnd', label: 'Active Hours End (0-23)', type: 'number', placeholder: '18', default: '18', required: true },
      { name: 'NoAutoRebootWithLoggedOnUsers', label: 'Prevent Reboot if User Logged On', type: 'checkbox', default: true }
    ]
  },
  { 
    id: 'scr-patch-reset', 
    title: 'Reset Windows Update Defaults', 
    description: 'Removes all custom Windows Update policies and resets to default behavior.', 
    lang: 'PowerShell', 
    icon: Activity, 
    color: 'text-slate-500', 
    risk: 'Low', 
    category: 'cat-patch',
    content: `
      $wuPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\WindowsUpdate"
      $auPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\WindowsUpdate\\AU"
      Remove-ItemProperty -Path $wuPath -Name "DeferQualityUpdates" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "DeferQualityUpdatesPeriodinDays" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "DeferFeatureUpdates" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "DeferFeatureUpdatesPeriodinDays" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "SetActiveHours" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "ActiveHoursStart" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $wuPath -Name "ActiveHoursEnd" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $auPath -Name "NoAutoRebootWithLoggedOnUsers" -ErrorAction SilentlyContinue
      Remove-ItemProperty -Path $auPath -Name "AUOptions" -ErrorAction SilentlyContinue
      Restart-Service wuauserv -Force
    `
  },

  // Advanced Configs
  { 
    id: 'scr-create-user', 
    title: 'Create User (Admin/Standard)', 
    description: 'Creates a local user with non-expiring password. Can optionally promote to Administrator.', 
    lang: 'PowerShell', 
    icon: Users, 
    color: 'text-purple-500', 
    risk: 'Medium', 
    category: 'cat-policies',
    content: `
      param([string]$username, [string]$initialPassword, [string]$userType, [string]$passwordPolicy)
      if ([string]::IsNullOrWhiteSpace($initialPassword)) { throw 'Initial password is required.' }
      if ([string]::IsNullOrWhiteSpace($username)) { throw 'Username is required.' }
      if ($username -match '@') { $username = ($username -split '@')[0] }
      $Password = ConvertTo-SecureString $initialPassword -AsPlainText -Force
      New-LocalUser -Name $username -Password $Password -FullName $username -Description "Local User" -PasswordNeverExpires ($passwordPolicy -eq 'Password Never Expires')
      if ($userType -eq 'Administrator') { Add-LocalGroupMember -Group "Administrators" -Member $username }
    `,
    params: [
      { name: 'username', label: 'New Username', type: 'text', placeholder: 'e.g. jdoe', required: true },
      { name: 'initialPassword', label: 'Initial Password', type: 'password', placeholder: '••••••••', required: true },
      { name: 'userType', label: 'Account Type', type: 'select', options: ['Standard User', 'Administrator'], default: 'Standard User' },
      { name: 'passwordPolicy', label: 'Password Policy', type: 'select', options: ['Password Never Expires', 'User Must Change at Next Logon'], default: 'Password Never Expires' }
    ]
  },
  { 
    id: 'scr-delete-user', 
    title: 'Delete User', 
    description: 'Removes a local user account from the system.', 
    lang: 'PowerShell', 
    icon: Users, 
    color: 'text-orange-500', 
    risk: 'High', 
    category: 'cat-policies',
    content: `param([string]$username) Remove-LocalUser -Name $username`,
    params: [
      { name: 'username', label: 'Username to Delete', type: 'text', placeholder: 'e.g. olduser', required: true }
    ]
  },

  // Security & Compliance
  { 
    id: 'scr-block-install', 
    title: 'Aggressively Block Installations', 
    description: 'Tightens UAC and blocks per-user MSI. Does not stop Hub Apps & Deployment (SYSTEM silent install). Local admins still get a UAC consent prompt. The Buntu companion can show a “contact administrator / Raise ticket” notice when someone runs an installer themselves.', 
    lang: 'PowerShell', 
    icon: Shield, 
    color: 'text-red-500', 
    risk: 'High', 
    category: 'cat-policies',
    content: `
      $uacPath = "HKLM:\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Policies\\System"
      if (-not (Test-Path $uacPath)) { New-Item -Path $uacPath -Force | Out-Null }
      Set-ItemProperty -Path $uacPath -Name "EnableInstallerDetection" -Value 1 -Type DWord
      Set-ItemProperty -Path $uacPath -Name "ConsentPromptBehaviorUser" -Value 3 -Type DWord
      Set-ItemProperty -Path $uacPath -Name "ConsentPromptBehaviorAdmin" -Value 2 -Type DWord
      Set-ItemProperty -Path $uacPath -Name "EnableLUA" -Value 1 -Type DWord
      $msiPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer"
      if (-not (Test-Path $msiPath)) { New-Item -Path $msiPath -Force | Out-Null }
      Set-ItemProperty -Path $msiPath -Name "DisableUserInstalls" -Value 1 -Type DWord
      Stop-Process -Name explorer -Force
    `
  },
  { 
    id: 'scr-enable-install', 
    title: 'Revert Script (Enable Installation)', 
    description: 'Reverts MSI restrictions, removes DisallowRun blocks, and resets UAC to Windows defaults.', 
    lang: 'PowerShell', 
    icon: Shield, 
    color: 'text-green-500', 
    risk: 'Medium', 
    category: 'cat-policies',
    content: `
      $msiPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer"
      if (Test-Path $msiPath) { Remove-ItemProperty -Path $msiPath -Name "DisableMSI" -ErrorAction SilentlyContinue; Remove-ItemProperty -Path $msiPath -Name "DisableUserInstalls" -ErrorAction SilentlyContinue }
      $explorerPath = "HKCU:\\Software\\Microsoft\\Windows\\CurrentVersion\\Policies\\Explorer"
      if (Test-Path $explorerPath) { Set-ItemProperty -Path $explorerPath -Name "DisallowRun" -Value 0 -Type DWord }
      $uacPath = "HKLM:\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Policies\\System"
      if (Test-Path $uacPath) { Set-ItemProperty -Path $uacPath -Name "ConsentPromptBehaviorAdmin" -Value 5 -Type DWord; Set-ItemProperty -Path $uacPath -Name "ConsentPromptBehaviorUser" -Value 3 -Type DWord }
      Stop-Process -Name explorer -Force
    `
  },
  { 
    id: 'scr-usb-strict', 
    title: 'Disable USB Storage (Strict)', 
    description: 'Blocks USB mass-storage only (USBSTOR + Removable Disks). Leaves mouse, keyboard, and other HID devices working.', 
    lang: 'PowerShell', 
    icon: WifiOff, 
    color: 'text-red-500', 
    risk: 'High', 
    category: 'cat-policies',
    content: `
      # Storage-only block. Do NOT set DeviceInstall\\Restrictions\\DenyRemovableDevices —
      # that also blocks USB mice/keyboards (PnP "removable" HID).
      $usbStorPath = "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\USBSTOR"
      if (-not (Test-Path $usbStorPath)) { New-Item -Path $usbStorPath -Force | Out-Null }
      Set-ItemProperty -Path $usbStorPath -Name "Start" -Value 4 -Type DWord

      # Clear any prior over-broad PnP deny that breaks HID
      $deviceInstallPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\DeviceInstall\\Restrictions"
      if (Test-Path $deviceInstallPath) {
        Remove-ItemProperty -Path $deviceInstallPath -Name "DenyRemovableDevices" -ErrorAction SilentlyContinue
      }

      # Removable Disks class only (USB flash / external HDD) — not HID
      $removableDisks = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\RemovableStorageDevices\\{53f5630d-b6bf-11d0-94f2-00a0c91efb8b}"
      if (-not (Test-Path $removableDisks)) { New-Item -Path $removableDisks -Force | Out-Null }
      Set-ItemProperty -Path $removableDisks -Name "Deny_Read" -Value 1 -Type DWord
      Set-ItemProperty -Path $removableDisks -Name "Deny_Write" -Value 1 -Type DWord

      # Drop global Deny_All if present (can be broader than needed)
      $removableStoragePath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\RemovableStorageDevices"
      if (Test-Path $removableStoragePath) {
        Remove-ItemProperty -Path $removableStoragePath -Name "Deny_All" -ErrorAction SilentlyContinue
      }

      # Restart USBSTOR is already disabled; refresh explorer UI only
      Stop-Process -Name explorer -Force -ErrorAction SilentlyContinue
      Write-Output "USB mass storage blocked; HID devices left enabled."
    `
  },
  { 
    id: 'scr-usb-enable', 
    title: 'Enable USB Storage', 
    description: 'Re-enables USBSTOR and removes Removable Disks deny policies. Also clears any leftover DenyRemovableDevices that would break mouse/keyboard.', 
    lang: 'PowerShell', 
    icon: CheckCircle, 
    color: 'text-green-500', 
    risk: 'Medium', 
    category: 'cat-policies',
    content: `
      $usbStorPath = "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\USBSTOR"
      if (-not (Test-Path $usbStorPath)) { New-Item -Path $usbStorPath -Force | Out-Null }
      Set-ItemProperty -Path $usbStorPath -Name "Start" -Value 3 -Type DWord

      # Fixed path (was SOFTWARE:\\Policies which never matched) + clear HID-breaking deny
      $deviceInstallPath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\DeviceInstall\\Restrictions"
      if (Test-Path $deviceInstallPath) {
        Remove-ItemProperty -Path $deviceInstallPath -Name "DenyRemovableDevices" -ErrorAction SilentlyContinue
      }

      $removableStoragePath = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\RemovableStorageDevices"
      if (Test-Path $removableStoragePath) {
        Remove-ItemProperty -Path $removableStoragePath -Name "Deny_All" -ErrorAction SilentlyContinue
      }
      $removableDisks = "HKLM:\\SOFTWARE\\Policies\\Microsoft\\Windows\\RemovableStorageDevices\\{53f5630d-b6bf-11d0-94f2-00a0c91efb8b}"
      if (Test-Path $removableDisks) {
        Remove-ItemProperty -Path $removableDisks -Name "Deny_Read" -ErrorAction SilentlyContinue
        Remove-ItemProperty -Path $removableDisks -Name "Deny_Write" -ErrorAction SilentlyContinue
      }

      # Ensure USBSTOR service can start
      try { Start-Service -Name "USBSTOR" -ErrorAction SilentlyContinue } catch {}
      Stop-Process -Name explorer -Force -ErrorAction SilentlyContinue
      Write-Output "USB storage re-enabled; DenyRemovableDevices cleared for HID recovery."
    `
  }
];

/** Scripts for Standard Baseline > Policies (exported for PolicyHub Standard Policies view). */
export const policyBaselineScripts = allScripts.filter(s => s.category === 'cat-policies');

const customIconRegistry = {
  terminal: Terminal,
  file: FileText,
  shield: Shield
};

// --- Components ---
const TreeNode = ({ node, level, selectedId, onSelect }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;

  const getIcon = (node) => {
    if (node.type === 'root') return <Layers size={14} className="mr-2 text-blue-400" />;
    if (node.type === 'root-baseline') return <FileText size={14} className="mr-2 text-purple-400" />;
    if (node.type === 'category') return <Folder size={14} className="mr-2 text-slate-500" />;
    // Use Font Awesome icons matching enrollment page
    if (node.id === 'plat-windows') return <i className="fab fa-windows mr-2 text-blue-500" style={{ fontSize: '14px' }} />;
    if (node.id === 'plat-linux') return <i className="fab fa-linux mr-2 text-orange-500" style={{ fontSize: '14px' }} />;
    if (node.id === 'plat-macos') return <i className="fab fa-apple mr-2 text-slate-600" style={{ fontSize: '14px' }} />;
    if (node.id === 'plat-android') return <i className="fab fa-android mr-2 text-green-500" style={{ fontSize: '14px' }} />;
    if (node.id === 'plat-ios') return <i className="fab fa-app-store-ios mr-2 text-gray-500" style={{ fontSize: '14px' }} />;
    if (node.type === 'dept') return <Briefcase size={14} className="mr-2 text-indigo-400" />;
    if (node.type === 'folder') return <Folder size={14} className="mr-2 text-purple-400" />;
    if (node.type === 'baseline-group') return <Zap size={14} className="mr-2 text-yellow-500" />;
    if (node.type === 'template') return <FileText size={14} className="mr-2 text-slate-400" />;
    if (node.type === 'analytics-group') return <FileText size={14} className="mr-2 text-blue-400" />;
    if (node.type === 'report') return <FileText size={14} className="mr-2 text-slate-300" />;
    if (node.type === 'custom-reports') return <FileText size={14} className="mr-2 text-green-400" />;
    return <Box size={14} className="mr-2 text-slate-500" />;
  };

  return (
    <div>
      <div
        className={`flex items-center py-1.5 px-2 cursor-pointer transition-colors rounded-md mx-2 ${isSelected ? 'bg-blue-600 text-white font-medium shadow-md shadow-blue-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        <span
          className="mr-1 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {hasChildren ? (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-3.5 block"></span>}
        </span>
        {getIcon(node)}
        <span className="text-sm truncate">{node.name}</span>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} level={level + 1} selectedId={selectedId} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

// Device Import/Export Modal Component
const DeviceImportModal = ({ isOpen, onClose, collectionId, collectionName, devices, onAddDevices }) => {
  const [importMode, setImportMode] = useState('single');
  const [singleDeviceId, setSingleDeviceId] = useState('');
  const [multipleDeviceIds, setMultipleDeviceIds] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [fileError, setFileError] = useState('');

  if (!isOpen) return null;

  const handleFileUpload = (event, fileType) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (fileType === 'text') {
        setFileContent(content);
        setFileError('');
      } else if (fileType === 'excel' || file.name.endsWith('.csv')) {
        // Handle CSV/Excel files
        setFileContent(content);
        setFileError('');
      } else {
        setFileError('Unsupported file type. Please use .txt, .csv, or .xlsx files.');
      }
    };
    reader.onerror = () => {
      setFileError('Error reading file.');
    };
    
    if (fileType === 'text' || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsText(file); // CSV files are text
    }
  };

  const parseDeviceIds = (input) => {
    // Parse device IDs from various formats
    const lines = input.split(/[\n\r,;]/).map(line => line.trim()).filter(Boolean);
    return lines;
  };

  const handleImport = () => {
    let deviceIds = [];
    
    if (importMode === 'single') {
      if (!singleDeviceId.trim()) {
        setFileError('Please enter a device ID.');
        return;
      }
      deviceIds = [singleDeviceId.trim()];
    } else if (importMode === 'multiple') {
      if (!multipleDeviceIds.trim()) {
        setFileError('Please enter device IDs.');
        return;
      }
      deviceIds = parseDeviceIds(multipleDeviceIds);
    } else if (importMode === 'excel' || importMode === 'text') {
      if (!fileContent.trim()) {
        setFileError('Please upload a file.');
        return;
      }
      deviceIds = parseDeviceIds(fileContent);
    }

    if (deviceIds.length === 0) {
      setFileError('No valid device IDs found.');
      return;
    }

    onAddDevices(deviceIds, collectionId);
    onClose();
    // Reset form
    setSingleDeviceId('');
    setMultipleDeviceIds('');
    setFileContent('');
    setFileError('');
  };

  const handleExport = (format) => {
    const collectionDevices = devices.filter(d => d.dept === collectionId);
    
    if (collectionDevices.length === 0) {
      setFileError('No devices in this collection to export.');
      return;
    }

    let content = '';
    let filename = '';
    let mimeType = '';

    if (format === 'csv' || format === 'excel') {
      // CSV format (Excel can open CSV)
      const headers = ['Device ID', 'Name', 'OS', 'Platform', 'Status', 'IP Address', 'Department'];
      content = headers.join(',') + '\n';
      collectionDevices.forEach(device => {
        const row = [
          device.id || device.device_id || '',
          device.name || '',
          device.os || '',
          device.platform || '',
          device.status || '',
          device.ip || '',
          device.dept || ''
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
        content += row + '\n';
      });
      filename = `${collectionName || 'devices'}_${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    } else if (format === 'text') {
      // Plain text format
      content = collectionDevices.map(device => 
        `${device.id || device.device_id || ''}\t${device.name || ''}\t${device.os || ''}\t${device.status || ''}`
      ).join('\n');
      filename = `${collectionName || 'devices'}_${new Date().toISOString().split('T')[0]}.txt`;
      mimeType = 'text/plain';
    }

    // Create download
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Upload size={18} className="text-slate-500" />
            Add Devices to "{collectionName}"
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {/* Mode Selection Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-200">
            <button
              onClick={() => setImportMode('single')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                importMode === 'single' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Single Device
            </button>
            <button
              onClick={() => setImportMode('multiple')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                importMode === 'multiple' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Multiple Devices
            </button>
            <button
              onClick={() => setImportMode('excel')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                importMode === 'excel' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Excel/CSV
            </button>
            <button
              onClick={() => setImportMode('text')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                importMode === 'text' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Text File
            </button>
          </div>

          {/* Import Content */}
          <div className="space-y-4">
            {importMode === 'single' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Device ID
                </label>
                <input
                  type="text"
                  value={singleDeviceId}
                  onChange={(e) => setSingleDeviceId(e.target.value)}
                  placeholder="Enter device ID (e.g., D-WIN-01)"
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {importMode === 'multiple' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Device IDs (one per line or comma-separated)
                </label>
                <textarea
                  value={multipleDeviceIds}
                  onChange={(e) => setMultipleDeviceIds(e.target.value)}
                  placeholder="D-WIN-01, D-WIN-02, D-WIN-03&#10;or&#10;D-WIN-01&#10;D-WIN-02&#10;D-WIN-03"
                  className="w-full border border-slate-300 rounded-md p-2 h-32 resize-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {(importMode === 'excel' || importMode === 'text') && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Upload {importMode === 'excel' ? 'Excel/CSV File' : 'Text File'}
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept={importMode === 'excel' ? '.csv,.xlsx,.xls' : '.txt'}
                    onChange={(e) => handleFileUpload(e, importMode)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload size={32} className="text-slate-400" />
                    <span className="text-sm text-slate-600">
                      Click to upload or drag and drop
                    </span>
                    <span className="text-xs text-slate-400">
                      {importMode === 'excel' ? 'CSV, XLSX, XLS files' : 'TXT files'}
                    </span>
                  </label>
                </div>
                {fileContent && (
                  <div className="mt-4 p-3 bg-slate-50 rounded border border-slate-200">
                    <p className="text-xs text-slate-600 mb-2">File content preview (first 200 chars):</p>
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {fileContent.substring(0, 200)}{fileContent.length > 200 ? '...' : ''}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {fileError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {fileError}
              </div>
            )}
          </div>

        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Devices
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardCard = ({ icon: Icon, iconClass, title, count, color, onClick, subtext }) => (
  <div
    onClick={onClick}
    className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group flex items-center gap-4"
  >
    <div className={`p-3.5 rounded-xl ${color} bg-opacity-10 group-hover:bg-opacity-20 transition-colors shrink-0`}>
      {iconClass ? (
        <i className={`${iconClass} ${color.replace('bg-', 'text-')}`} style={{ fontSize: '32px' }} />
      ) : (
        <Icon size={32} className={color.replace('bg-', 'text-')} />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-500 mb-0.5">{title}</p>
      <h3 className="text-2xl font-bold text-slate-800 leading-none mb-1">
        {count} <span className="text-sm font-normal text-slate-400">devices</span>
      </h3>
      {subtext && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
          <p className="text-xs font-medium text-slate-500">{subtext}</p>
        </div>
      )}
    </div>
  </div>
);

const AdHocScriptView = ({ devices, deviceGroups }) => {
  const [selectedOS, setSelectedOS] = useState('');
  const [targetMode, setTargetMode] = useState('single');
  const [selectedSingleDevice, setSelectedSingleDevice] = useState('');
  const [multipleDeviceInput, setMultipleDeviceInput] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [scriptContent, setScriptContent] = useState('');
  const [scriptType, setScriptType] = useState('powershell');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [commandResults, setCommandResults] = useState([]);
  const [polling, setPolling] = useState(false);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const scriptModes = [
    { value: 'powershell', label: 'PowerShell', extension: 'ps1', placeholder: '# PowerShell script' },
    { value: 'bash', label: 'Bash', extension: 'sh', placeholder: '#!/bin/bash' },
    { value: 'cmd', label: 'CMD', extension: 'cmd', placeholder: 'REM Windows CMD script' },
    { value: 'shell', label: 'Shell', extension: 'sh', placeholder: '#!/bin/sh' },
    { value: 'sql', label: 'SQL', extension: 'sql', placeholder: 'SELECT * FROM devices;' }
  ];

  const osOptions = [
    { value: 'windows', label: 'Windows', platform: 'plat-windows' },
    { value: 'linux', label: 'Linux', platform: 'plat-linux' },
    { value: 'macos', label: 'macOS', platform: 'plat-macos' },
    { value: 'android', label: 'Android', platform: 'plat-android' },
    { value: 'ios', label: 'iOS', platform: 'plat-ios' }
  ];

  const currentMode = scriptModes.find((mode) => mode.value === scriptType) || scriptModes[0];
  const selectedPlatformId = osOptions.find((opt) => opt.value === selectedOS)?.platform || null;
  const osLabel = osOptions.find((opt) => opt.value === selectedOS)?.label || 'Device';

  const filteredDevices = useMemo(
    () => (selectedPlatformId ? devices.filter((d) => deviceMatchesOS(d, selectedPlatformId)) : []),
    [devices, selectedPlatformId]
  );
  const deviceIdSet = useMemo(() => new Set(filteredDevices.map((d) => d.id)), [filteredDevices]);
  const groupDeviceCounts = useMemo(() => {
    const counts = {};
    filteredDevices.forEach((device) => {
      counts[device.dept] = (counts[device.dept] || 0) + 1;
    });
    return counts;
  }, [filteredDevices]);

  const rootNode = deviceGroups?.[0];
  const deptCategory = rootNode?.children?.find((c) => c.id === 'cat-depts');
  const departmentNodes = deptCategory?.children || [];
  const folderNodes = departmentNodes.filter((node) => node.type === 'folder');
  const rootGroupNodes = departmentNodes.filter((node) => node.type === 'dept');

  const folderOptions = useMemo(
    () =>
      folderNodes
        .map((folder) => {
          const eligibleChildren = (folder.children || []).filter((child) => groupDeviceCounts[child.id]);
          if (!eligibleChildren.length) return null;
          return { ...folder, eligibleChildren };
        })
        .filter(Boolean),
    [folderNodes, groupDeviceCounts]
  );

  const rootGroupOptions = useMemo(
    () => rootGroupNodes.filter((group) => groupDeviceCounts[group.id]),
    [rootGroupNodes, groupDeviceCounts]
  );

  const selectedFolder = folderOptions.find((folder) => folder.id === selectedFolderId);
  const folderGroupOptions = selectedFolder?.eligibleChildren || [];

  useEffect(() => {
    setSelectedSingleDevice('');
    setMultipleDeviceInput('');
    setSelectedFolderId('');
    setSelectedGroupId('');
  }, [selectedPlatformId]);

  useEffect(() => {
    setSelectedGroupId('');
  }, [selectedFolderId]);

  const parseMultipleIds = () => {
    const entries = multipleDeviceInput
      .split(/[\s,]+/)
      .map((val) => val.trim())
      .filter(Boolean);
    const unique = Array.from(new Set(entries));
    return unique.filter((id) => deviceIdSet.has(id));
  };

  const resolveGroupDeviceIds = () => {
    if (!selectedGroupId) return [];
    if (selectedGroupId.startsWith('plat-')) {
      return filteredDevices.map((d) => d.id);
    }
    return filteredDevices.filter((device) => device.dept === selectedGroupId).map((device) => device.id);
  };

  const computeTargetDeviceIds = () => {
    if (!selectedPlatformId) return [];
    if (targetMode === 'single') {
      return selectedSingleDevice ? [selectedSingleDevice] : [];
    }
    if (targetMode === 'multiple') {
      return parseMultipleIds();
    }
    if (targetMode === 'group') {
      return resolveGroupDeviceIds();
    }
    return [];
  };

  const handleRun = async () => {
    if (!selectedPlatformId) {
      setResult({ success: false, output: 'Select an OS target before running the script.' });
      return;
    }
    if (!scriptContent.trim()) {
      setResult({ success: false, output: 'Script content cannot be empty.' });
      return;
    }
    const targetDeviceIds = computeTargetDeviceIds();
    if (!targetDeviceIds.length) {
      setResult({ success: false, output: 'No matching devices found for the selected target.' });
      return;
    }

    setExecuting(true);
    setResult(null);
    setCommandResults([]);
    try {
      const responses = [];
      for (const deviceId of targetDeviceIds) {
        const response = await apiService.executeGoAgentCommand(deviceId, {
          command_type: scriptType,
          command: scriptContent,
          timeout_seconds: 60,
          created_by: 'dashboard-user'
        });
        responses.push({
          deviceId,
          status: response?.status || 'queued',
          commandId: response?.command_id
        });
      }
      const summary = responses
        .map(
          (entry) =>
            `• ${entry.deviceId}: ${entry.status}${entry.commandId ? ` (ID: ${entry.commandId})` : ''}`
        )
        .join('\n');
      setResult({
        success: true,
        output: `Dispatched ${responses.length} command(s) to ${osLabel} devices:\n${summary}`
      });
      
      // Start polling for results
      setCommandResults(responses);
      setPolling(true);
      pollCommandResults(responses);
    } catch (e) {
      setResult({ success: false, output: 'Error executing command: ' + (e.message || e) });
    } finally {
      setExecuting(false);
    }
  };

  const pollCommandResults = async (responses, attempt = 0) => {
    if (attempt > 60) { // Stop after 60 attempts (5 minutes)
      setPolling(false);
      return;
    }

    try {
      const updatedResults = await Promise.all(
        responses.map(async (resp) => {
          try {
            // Fetch all commands (no status filter) to find the specific command
            // Backend expects single status value, not comma-separated
            const commands = await apiService.getGoAgentCommands(resp.deviceId, {
              limit: 100
            });
            const command = commands.items?.find(cmd => cmd.command_id === resp.commandId);
            if (command) {
              return {
                ...resp,
                status: command.status,
                result: command.result,
                error_message: command.error_message,
                completed_at: command.completed_at
              };
            }
            return resp;
          } catch (error) {
            console.error(`Error polling command for device ${resp.deviceId}:`, error);
            return resp;
          }
        })
      );

      setCommandResults(updatedResults);

      // Check if all commands are completed or failed (use outcome, not raw agent status)
      const allCompleted = updatedResults.every(r => {
        const outcome = classifyCommandOutcome(r.status, r.result, r.error_message);
        return outcome === 'succeeded' || outcome === 'failed';
      });

      if (allCompleted) {
        setPolling(false);
        const successCount = updatedResults.filter(r =>
          classifyCommandOutcome(r.status, r.result, r.error_message) === 'succeeded'
        ).length;
        const failedCount = updatedResults.filter(r =>
          classifyCommandOutcome(r.status, r.result, r.error_message) === 'failed'
        ).length;
        const resultSummary = updatedResults.map(r => {
          const deviceName = filteredDevices.find(d => d.id === r.deviceId)?.name || r.deviceId;
          const outcome = classifyCommandOutcome(r.status, r.result, r.error_message);
          let statusLine = `• ${deviceName}: ${outcome === 'succeeded' ? 'completed' : outcome}`;
          if (outcome === 'succeeded' && r.result) {
            const output = typeof r.result === 'object' ? r.result.output || JSON.stringify(r.result, null, 2) : r.result;
            statusLine += `\n  Output: ${output.substring(0, 200)}${output.length > 200 ? '...' : ''}`;
          } else if (outcome === 'failed') {
            const errText = r.error_message || (typeof r.result === 'object' ? r.result?.output : r.result);
            if (errText) statusLine += `\n  Error: ${String(errText).substring(0, 200)}`;
          }
          return statusLine;
        }).join('\n\n');
        
        setResult({
          success: successCount > 0,
          output: `Execution Complete: ${successCount} succeeded, ${failedCount} failed\n\n${resultSummary}`
        });
      } else {
        // Continue polling
        setTimeout(() => pollCommandResults(updatedResults, attempt + 1), 5000);
      }
    } catch (error) {
      console.error('Error polling command results:', error);
      setPolling(false);
    }
  };

  // Load execution history from API
  const loadExecutionHistory = async () => {
    if (!selectedSingleDevice && targetMode === 'single') return;
    if (!selectedPlatformId) return;
    
    setLoadingHistory(true);
    try {
      const deviceIds = targetMode === 'single' 
        ? [selectedSingleDevice]
        : targetMode === 'multiple'
        ? multipleDeviceInput.split(/[,\n]/).map(id => id.trim()).filter(Boolean)
        : [];
      
      if (deviceIds.length === 0) {
        setExecutionHistory([]);
        return;
      }
      
      const historyPromises = deviceIds.map(async (deviceId) => {
        try {
          const response = await apiService.getGoAgentCommands(deviceId, { limit: 50 });
          return (response.items || []).map(cmd => ({
            ...cmd,
            deviceId,
            deviceName: filteredDevices.find(d => d.id === deviceId)?.name || deviceId
          }));
        } catch (error) {
          console.error(`Error loading history for device ${deviceId}:`, error);
          return [];
        }
      });
      
      const allHistory = await Promise.all(historyPromises);
      const flattened = allHistory.flat().sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setExecutionHistory(flattened);
    } catch (error) {
      console.error('Error loading execution history:', error);
      setExecutionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when device selection changes
  useEffect(() => {
    if (showHistory && selectedPlatformId) {
      loadExecutionHistory();
    }
  }, [showHistory, selectedSingleDevice, selectedPlatformId, targetMode]);

  const canRun =
    !!selectedPlatformId &&
    !!scriptContent.trim() &&
    ((targetMode === 'single' && selectedSingleDevice) ||
      (targetMode === 'multiple' && multipleDeviceInput.trim().length > 0) ||
      (targetMode === 'group' && selectedGroupId));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full flex flex-col animate-in fade-in zoom-in duration-300">
      <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
        <Terminal size={20} className="text-blue-600" />
        Run Custom Script
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
        <div className="md:col-span-1 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Select Target OS</label>
            <select
              className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={selectedOS}
              onChange={(e) => setSelectedOS(e.target.value)}
            >
              <option value="">-- Choose OS Family --</option>
              {osOptions.map((os) => (
                <option key={os.value} value={os.value}>
                  {os.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Only folders, groups, and devices that match the selected OS will be available.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Target Scope</label>
            <div className="flex gap-2">
              {['single', 'multiple', 'group'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setTargetMode(mode)}
                  className={`flex-1 text-xs py-1.5 rounded border ${
                    targetMode === mode
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-300'
                  }`}
                >
                  {mode === 'single' && 'Single'}
                  {mode === 'multiple' && 'Multiple'}
                  {mode === 'group' && 'Group'}
                </button>
              ))}
            </div>
          </div>

          {targetMode === 'single' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">{osLabel} Device</label>
              <DevicePicker
                devices={filteredDevices}
                value={selectedSingleDevice}
                onChange={setSelectedSingleDevice}
                disabled={!selectedPlatformId}
                emptyMessage={selectedPlatformId ? 'No devices for this OS.' : 'Choose an OS to see device options.'}
              />
              {!selectedPlatformId && (
                <p className="text-xs text-amber-600 mt-1">Choose an OS to see device options.</p>
              )}
            </div>
          )}

          {targetMode === 'multiple' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Select Devices</label>
              <DevicePicker
                mode="multiple"
                devices={filteredDevices}
                value={
                  multipleDeviceInput
                    ? multipleDeviceInput.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
                    : []
                }
                onChange={(ids) => setMultipleDeviceInput(ids.join(', '))}
                disabled={!selectedPlatformId}
                emptyMessage={selectedPlatformId ? 'No devices for this OS.' : 'Choose an OS first.'}
              />
              {!selectedPlatformId && (
                <p className="text-xs text-amber-600 mt-1">Select an OS before choosing devices.</p>
              )}
            </div>
          )}

          {targetMode === 'group' && (
            <div className="space-y-3">
              {!selectedPlatformId ? (
                <p className="text-xs text-amber-600">Choose an OS above to see folders and groups.</p>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Folders</label>
                    <select
                      className="w-full text-sm border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      value={selectedFolderId}
                      onChange={(e) => setSelectedFolderId(e.target.value || '')}
                    >
                      <option value="">-- All Folders --</option>
                      {folderOptions.map((folder) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name} ({folder.eligibleChildren?.length || 0} groups)
                        </option>
                      ))}
                    </select>
                    {!folderOptions.length && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        No folders have {osLabel} devices yet.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Groups</label>
                    <select
                      className="w-full text-sm border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                    >
                      <option value="">-- Select Group --</option>
                      {folderGroupOptions.length > 0 && (
                        <optgroup label={`Groups in ${selectedFolder?.name || ''}`}>
                          {folderGroupOptions.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name} ({groupDeviceCounts[group.id] || 0})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {rootGroupOptions.length > 0 && (
                        <optgroup label="Root Level Groups">
                          {rootGroupOptions.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name} ({groupDeviceCounts[group.id] || 0})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Entire OS">
                        <option value={selectedPlatformId}>
                          All {osLabel} Devices ({filteredDevices.length})
                        </option>
                      </optgroup>
                    </select>
                    {!selectedGroupId && (
                      <p className="text-xs text-slate-500 mt-1">
                        Select a folder (optional) and then choose a group to target.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Execution Options</label>
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <span className="w-40 text-slate-500">Script Type</span>
                <select
                  className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
                  value={scriptType}
                  onChange={(e) => setScriptType(e.target.value)}
                >
                  {scriptModes.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" className="rounded text-blue-600" defaultChecked />
                Run as Administrator
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" className="rounded text-blue-600" />
                Wait for Result
              </label>
            </div>
          </div>

          <button
            onClick={handleRun}
            disabled={executing || !canRun}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold shadow-md hover:bg-blue-700 transition-all flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {executing ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <Play size={16} />
            )}
            {executing ? 'Executing...' : 'Run Script'}
          </button>
        </div>

        <div className="md:col-span-2 flex flex-col gap-4 h-full min-h-[400px]">
          <div className="flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-slate-700">{currentMode.label} Script</label>
              <span className="text-xs text-slate-400 font-mono">{currentMode.extension}</span>
            </div>
            <textarea
              className="flex-1 w-full border border-slate-300 rounded-lg p-4 font-mono text-sm bg-slate-900 text-green-400 focus:ring-2 focus:ring-blue-500 outline-none resize-none shadow-inner leading-relaxed"
              value={scriptContent}
              onChange={(e) => setScriptContent(e.target.value)}
              placeholder={currentMode.placeholder}
              spellCheck={false}
            />
          </div>

          {result && (
            <div
              className={`p-4 rounded-lg border ${
                result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              } animate-in slide-in-from-bottom-2`}
            >
              <div className="flex items-center gap-2 mb-2 font-semibold text-sm">
                {polling ? (
                  <Activity size={14} className="text-blue-600 animate-spin" />
                ) : result.success ? (
                  <CheckCircle size={14} className="text-green-600" />
                ) : (
                  <AlertCircle size={14} className="text-red-600" />
                )}
                <span className={result.success ? 'text-green-800' : 'text-red-800'}>
                  {polling ? 'Polling for Results...' : result.success ? 'Execution Complete' : 'Execution Failed'}
                </span>
              </div>
              <pre
                className={`text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto p-2 rounded ${
                  result.success ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'
                }`}
              >
                {result.output}
              </pre>
              {commandResults.length > 0 && (
                <div className="mt-3 pt-3 border-t border-green-300">
                  <div className="text-xs font-semibold text-green-800 mb-2">Device Results:</div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {commandResults.map((cmd, idx) => {
                      const deviceName = filteredDevices.find(d => d.id === cmd.deviceId)?.name || cmd.deviceId;
                      const statusColors = {
                        'completed': 'text-green-700',
                        'failed': 'text-red-700',
                        'pending': 'text-blue-700',
                        'queued': 'text-yellow-700',
                        'running': 'text-indigo-700'
                      };
                      return (
                        <div key={idx} className="text-xs bg-white p-2 rounded border border-green-200">
                          <div className="font-semibold text-green-900">{deviceName}</div>
                          <div className={`${statusColors[cmd.status] || 'text-slate-700'}`}>
                            Status: <span className="font-medium">{cmd.status || 'pending'}</span>
                            {cmd.commandId && (
                              <span className="text-slate-500 ml-2">(ID: {cmd.commandId.substring(0, 8)}...)</span>
                            )}
                          </div>
                          {cmd.status === 'completed' && cmd.result && (
                            <div className="mt-2">
                              <div className="text-xs font-semibold text-slate-600 mb-1">Output:</div>
                              <div className="p-2 bg-slate-900 text-green-400 rounded font-mono text-xs max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {typeof cmd.result === 'object' ? (cmd.result.output || JSON.stringify(cmd.result, null, 2)) : String(cmd.result)}
                              </div>
                            </div>
                          )}
                          {cmd.status === 'pending' || cmd.status === 'queued' || cmd.status === 'running' ? (
                            <div className="mt-2 p-2 bg-blue-50 text-blue-700 rounded text-xs">
                              {cmd.status === 'running' ? '⏳ Script is executing...' : '⏳ Waiting for results...'}
                            </div>
                          ) : null}
                          {cmd.status === 'failed' && (
                            <div className="mt-2">
                              {cmd.error_message ? (
                                <div className="p-2 bg-red-50 text-red-700 rounded text-xs">
                                  <div className="font-semibold mb-1">Error:</div>
                                  <div>{cmd.error_message}</div>
                                </div>
                              ) : (
                                <div className="p-2 bg-red-50 text-red-700 rounded text-xs">Execution failed</div>
                              )}
                            </div>
                          )}
                          {cmd.completed_at && (
                            <div className="text-xs text-slate-500 mt-1">
                              Completed: {new Date(cmd.completed_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Execution History */}
        <div className="mt-6 border-t border-slate-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <History size={16} className="text-slate-600" />
              Execution History
            </h3>
            {executionHistory.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Clear all execution history?')) {
                    setExecutionHistory([]);
                    localStorage.removeItem('buntu_script_execution_history');
                  }
                }}
                className="text-xs text-slate-500 hover:text-red-600"
              >
                Clear History
              </button>
            )}
          </div>
          {executionHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No execution history yet. Run a script to see history here.
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {executionHistory.map((entry) => {
                const scriptPreview = entry.scriptContent.substring(0, 50) + (entry.scriptContent.length > 50 ? '...' : '');
                return (
                  <div key={entry.id} className="bg-slate-50 rounded-lg border border-slate-200 p-4 hover:bg-slate-100 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {entry.scriptType.toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-600">
                            {entry.os ? entry.os.toUpperCase() : 'N/A'} • {entry.targetMode}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs font-mono text-slate-600 mb-2">{scriptPreview}</div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className={`font-semibold ${entry.summary.succeeded > 0 ? 'text-green-600' : 'text-slate-600'}`}>
                            ✓ {entry.summary.succeeded} succeeded
                          </span>
                          {entry.summary.failed > 0 && (
                            <span className="font-semibold text-red-600">
                              ✗ {entry.summary.failed} failed
                            </span>
                          )}
                          <span className="text-slate-500">
                            {entry.summary.total} device{entry.summary.total !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Expand to show details
                          const details = entry.results.map(r => {
                            const deviceName = filteredDevices.find(d => d.id === r.deviceId)?.name || r.deviceId;
                            let resultText = `${deviceName}: ${r.status}`;
                            if (r.status === 'completed' && r.result) {
                              const output = typeof r.result === 'object' ? r.result.output || JSON.stringify(r.result, null, 2) : r.result;
                              resultText += `\nOutput: ${output}`;
                            } else if (r.status === 'failed' && r.error_message) {
                              resultText += `\nError: ${r.error_message}`;
                            }
                            return resultText;
                          }).join('\n\n');
                          alert(`Execution Details:\n\n${details}`);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function BuntuDashboard({ embedded = false, onNavigateToSettings }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [selectedNode, setSelectedNode] = useState({ id: 'dashboard', name: 'Overview', type: 'dashboard' });
  const [viewMode, setViewMode] = useState('devices');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [notification, setNotification] = useState(null);
  const [checkedDevices, setCheckedDevices] = useState({});
  const [logoError, setLogoError] = useState(false);
  const [policyModal, setPolicyModal] = useState({
    isOpen: false,
    deviceType: null,
    baselineType: null
  });
  const [customPolicies, setCustomPolicies] = useState({ general: {}, baselines: {} });
  const [policyHydrated, setPolicyHydrated] = useState(false);
  
  // Data States
  const [devices, setDevices] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Device Import/Export Modal State
  const [openActionMenu, setOpenActionMenu] = useState(null); // Track which device's action menu is open
  const [moveModal, setMoveModal] = useState({ isOpen: false, deviceIds: [], deviceNames: [] }); // Move modal state - supports bulk move
  
  const [usersModal, setUsersModal] = useState({ isOpen: false, deviceId: null, deviceName: null });
  const [deviceUsers, setDeviceUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [deviceImportModal, setDeviceImportModal] = useState({
    isOpen: false,
    collectionId: null,
    collectionName: null,
    importMode: 'single' // 'single', 'multiple', 'excel', 'text'
  });
  
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  
  // Custom Collections and Folders State (DB via tenant directory API)
  const [customCollections, setCustomCollections] = useState([]);
  const [customFolders, setCustomFolders] = useState([]);

  /** Persist folders + collections to Postgres (localStorage mirror updated by store). */
  const persistDirectory = async (folders, collections) => {
    const normalized = (Array.isArray(collections) ? collections : []).map((c) => ({
      ...c,
      deviceIds: Array.isArray(c.deviceIds) ? c.deviceIds : [],
      criticalDeviceIds: Array.isArray(c.criticalDeviceIds) ? c.criticalDeviceIds : [],
    }));
    setCustomFolders(folders);
    setCustomCollections(normalized);
    try {
      const saved = await saveDirectory(folders, normalized);
      setCustomFolders(saved.folders);
      setCustomCollections(saved.collections);
    } catch (e) {
      console.warn('[BuntuDashboard] directory persist failed:', e);
    }
  };

  const saveCustomCollections = (updated) => {
    void persistDirectory(customFolders, updated);
  };

  const saveCustomFolders = (updated) => {
    void persistDirectory(updated, customCollections);
  };

  /**
   * Mark/unmark devices as critical within a collection.
   * Critical flags are stored on the collection and drive honeycomb card status.
   */
  const setDevicesCriticalInCollection = (
    collectionId,
    collectionName,
    deviceIds,
    markAsCritical,
    folderId = null
  ) => {
    if (!collectionId || !deviceIds?.length) return;
    const ids = deviceIds.map(String);
    let updated = [...customCollections];
    let idx = updated.findIndex((c) => c.id === collectionId);
    if (idx < 0) {
      updated.push({
        id: collectionId,
        name: collectionName || collectionId,
        folderId: folderId ?? null,
        createdAt: new Date().toISOString(),
        criticalDeviceIds: [],
      });
      idx = updated.length - 1;
    }
    const current = new Set((updated[idx].criticalDeviceIds || []).map(String));
    ids.forEach((id) => {
      if (markAsCritical) current.add(id);
      else current.delete(id);
    });
    updated[idx] = {
      ...updated[idx],
      name: updated[idx].name || collectionName || collectionId,
      criticalDeviceIds: Array.from(current),
    };
    saveCustomCollections(updated);
    setNotification({
      title: markAsCritical ? 'Marked Critical' : 'Unmarked Critical',
      msg: markAsCritical
        ? `${ids.length} device(s) marked critical in "${updated[idx].name}". Honeycomb cards will show them as critical.`
        : `${ids.length} device(s) unmarked in "${updated[idx].name}".`,
      type: 'success',
    });
    setTimeout(() => setNotification(null), 3000);
  };

  const isDeviceCriticalInCollection = (collectionId, deviceId) => {
    if (!collectionId || !deviceId) return false;
    const col = customCollections.find((c) => c.id === collectionId);
    return (col?.criticalDeviceIds || []).map(String).includes(String(deviceId));
  };
  
  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu && !event.target.closest('.action-menu-container')) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenu]);

  // Helper function to calculate if device is online based on last_seen
  const isDeviceOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - lastSeenDate.getTime();
    return diffMs < 5 * 60 * 1000;
  };

  // Health classification: Active (<7d), Inactive (7-15d), Critical (>=15d)
  const getDeviceHealth = (lastSeen) => {
    if (!lastSeen) return 'critical';
    const diffMs = Date.now() - new Date(lastSeen).getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < 7) return 'active';
    if (diffDays < 15) return 'inactive';
    return 'critical';
  };

  // Relative time string for offline devices
  const formatRelativeTime = (lastSeen) => {
    if (!lastSeen) return 'Never seen';
    const diffMs = Date.now() - new Date(lastSeen).getTime();
    const mins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return 'Last seen just now';
    if (mins < 60) return `Last seen ${mins} min${mins > 1 ? 's' : ''} ago`;
    if (hrs < 24) return `Last seen ${hrs} hr${hrs > 1 ? 's' : ''} ago`;
    if (days < 30) return `Last seen ${days} day${days > 1 ? 's' : ''} ago`;
    return `Last seen ${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  };

  // --- API Integration ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Check health first
      const healthStatus = await apiService.checkHealth();
      
      // Fetch Dashboard Stats, Devices, Compliance, and Osquery health in parallel
      const [dashboardData, devicesData, compData, osqueryHealth] = await Promise.all([
        apiService.getEnhancedDeviceDashboard(),
        apiService.getDevices(),
        (typeof apiService.getDeviceCompliance === 'function' ? apiService.getDeviceCompliance() : Promise.resolve([])).catch(() => []),
        (typeof apiService.getOsqueryHealthSummary === 'function' ? apiService.getOsqueryHealthSummary() : Promise.resolve({ by_device: {} })).catch(() => ({ by_device: {} }))
      ]);
      
      // Set dashboard stats - include health status
      if (dashboardData) {
        setDashboardStats({
          ...dashboardData,
          health: healthStatus,
          apiOnline: !!healthStatus
        });
      } else if (healthStatus) {
        // If dashboard data fails but health check succeeds, show API is online
        setDashboardStats({
          health: healthStatus,
          apiOnline: true
        });
      } else {
        setDashboardStats(null);
      }
      
      if (devicesData && Array.isArray(devicesData)) {
        // Map API devices to Dashboard format
        const mappedDevices = devicesData.map(d => {
          // Fix device name: backend should handle this, but add extra safety check
          let deviceName = d.name || '';
          
          // If name still looks like a file path (backend fix didn't work), fix it here
          if (deviceName && (deviceName.includes('/') || deviceName.includes('\\') || deviceName.includes(':'))) {
            const metadata = d.device_metadata || {};
            // Priority: hostname from metadata > platform_device_id (if valid) > extract from path
            const hostname = metadata.hostname || metadata.Hostname;
            if (hostname && !hostname.includes('/') && !hostname.includes('\\') && !hostname.includes(':')) {
              deviceName = hostname;
            } else if (d.platform_device_id && !d.platform_device_id.includes('/') && !d.platform_device_id.includes('\\') && !d.platform_device_id.includes(':')) {
              deviceName = d.platform_device_id;
            } else {
              // Extract filename from path
              const pathParts = deviceName.split(/[/\\]/);
              deviceName = pathParts[pathParts.length - 1] || `Device-${d.device_id?.substring(0, 8) || 'Unknown'}`;
            }
          }
          
          // If still empty or invalid, use platform_device_id or hostname
          if (!deviceName || deviceName.trim() === '') {
            const metadata = d.device_metadata || {};
            deviceName = d.platform_device_id || 
                        metadata.hostname || 
                        metadata.Hostname ||
                        `Device-${d.device_id?.substring(0, 8) || 'Unknown'}`;
          }
          
          const deviceId = d.device_id;
          // Health metrics: prefer Osquery (telemetry/osquery-health-summary), fallback to Device API (Go agent/backend)
          const osq = (osqueryHealth?.by_device && osqueryHealth.by_device[deviceId]) || {};
          return {
            id: deviceId,
            name: deviceName,
            type: d.device_type || 'Workstation',
            os: d.os || 'Unknown',
            platform: mapPlatform(d.os, d.platform),
            status: d.status,
            cpu: osq.cpu ?? d.cpu_usage ?? 0,
            ram: osq.ram ?? d.memory_usage ?? 0,
            storage: osq.storage ?? d.disk_usage ?? 0,
            compliance_status: d.compliance_status,
            dept: d.department || d.device_metadata?.department || 'Unassigned',
            ip: d.ip_address || 'Unknown',
            last_seen: d.last_seen,
            platform_device_id: d.platform_device_id,
            hostname: d.device_metadata?.hostname || d.device_metadata?.Hostname,
            device_metadata: d.device_metadata || {},
            has_osquery: osq.has_osquery ?? false
          };
        });
        
        setDevices(mappedDevices);
      }
      if (compData && Array.isArray(compData)) {
        setComplianceData(compData);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      // Fallback to mock data is already set
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchData();
    
    // Set up auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadDirectory(), loadPolicyLibrary()])
      .then(([dir, lib]) => {
        if (cancelled) return;
        setCustomFolders(dir.folders || []);
        setCustomCollections(
          (dir.collections || []).map((c) => ({
            ...c,
            criticalDeviceIds: Array.isArray(c.criticalDeviceIds) ? c.criticalDeviceIds : [],
          }))
        );
        setCustomPolicies(lib || { general: {}, baselines: {} });
        setPolicyHydrated(true);
      })
      .catch((e) => {
        console.warn('[BuntuDashboard] directory load failed:', e);
        if (!cancelled) setPolicyHydrated(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!policyHydrated) return;
    savePolicyLibrary(customPolicies).catch((e) => {
      console.warn('[BuntuDashboard] policy library save failed:', e);
    });
  }, [customPolicies, policyHydrated]);

  const mapPlatform = (os, platform) => {
    if (!os) return 'plat-windows';
    const lower = os.toLowerCase();
    if (lower.includes('win')) return 'plat-windows';
    if (lower.includes('linux') || lower.includes('ubuntu') || lower.includes('debian')) return 'plat-linux';
    if (lower.includes('mac') || lower.includes('darwin') || lower.includes('osx')) return 'plat-macos';
    if (lower.includes('android')) return 'plat-android';
    if (lower.includes('ios') || lower.includes('iphone')) return 'plat-ios';
    return 'plat-windows';
  };

  const flattenCustomPolicyMap = (map = {}) => {
    return Object.entries(map).flatMap(([deviceType, policies]) => {
      if (!deviceType || !Array.isArray(policies)) return [];
      return policies.map((policy) => ({
        ...policy,
        icon: customIconRegistry[policy.iconName] || policy.icon || Terminal,
        deviceType,
        lang: policy.lang || policy.commandType || 'Custom',
        type: policy.type || 'script',
        color: policy.color || 'text-indigo-500',
        category: policy.category || 'custom',
        risk: policy.risk || 'Medium'
      }));
    });
  };

  // --- Helper: View States ---
  const isDashboard = selectedNode.id === 'dashboard';
  const isBaselineView = selectedNode.category === 'baseline';
  const isAnalyticsView = selectedNode.category === 'analytics';

  // --- Filter Logic ---
  const getFilteredDevices = () => {
    if (isDashboard || isBaselineView) return [];
    if (selectedNode.id === 'root') return devices;
    if (selectedNode.type === 'category') {
      if (selectedNode.id === 'cat-platforms') return devices;
      if (selectedNode.id === 'cat-depts') return devices;
    }
    if (selectedNode.id.startsWith('plat-')) return devices.filter(d => deviceMatchesOS(d, selectedNode.id));
    if (selectedNode.type === 'dept') return devices.filter(d => d.dept === selectedNode.id);
    if (selectedNode.type === 'folder') {
      // For folders, show all devices in collections within that folder
      const folderCollections = selectedNode.children || [];
      const collectionIds = folderCollections.map(c => c.id);
      return devices.filter(d => collectionIds.includes(d.dept));
    }
    return devices;
  };

  const getBaselineScripts = () => {
    if (!isBaselineView) return [];
    if (selectedNode.id === 'base-root') return allScripts; // Should not happen after structure change, but good for safety
    
    // Standard Baseline Group
    if (selectedNode.id === 'base-std') {
      return allScripts.filter(s => ['cat-policies', 'cat-patch'].includes(s.category));
    }
    // Specific Templates under Standard Baseline
    // For Policies, we'll show device type cards instead of scripts directly
    if (selectedNode.id === 'tmpl-policies') return allScripts.filter(s => s.category === 'cat-policies');
    if (selectedNode.id === 'tmpl-patch') return allScripts.filter(s => s.category === 'cat-patch');

    // For Advanced & Security groups, user requested they be kept in nav but their content is now in "Policies".
    // So if we click them, we might show the policies list or empty if that's strict "policies tab only".
    // Assuming "Policies tab only" means these groups are organizational or empty for now in this context.
    // I will make them show nothing to respect "shown under policies tab ONLY".
    if (selectedNode.id === 'base-adv') return []; 
    if (selectedNode.id === 'base-sec') return [];

    return allScripts;
  };

  // --- Stats Logic for Dashboard ---
  const getPlatformStats = (pid) => {
    const devs = devices.filter(d => deviceMatchesOS(d, pid));
    const online = devs.filter(d => isDeviceOnline(d.last_seen)).length;
    const total = devs.length;
    return { total, online, offline: total - online };
  };

  const filteredDevices = getFilteredDevices();
  const generalCustomScripts = flattenCustomPolicyMap(customPolicies.general || {});
  const scriptLibrary = isBaselineView ? getBaselineScripts() : allScripts;
  const quickActionScripts = !isBaselineView ? [...scriptLibrary, ...generalCustomScripts] : scriptLibrary;
  const selectedCount = Object.values(checkedDevices).filter(Boolean).length;

  const handleDeployClick = (script) => { setSelectedScript(script); setModalOpen(true); };

  const openPolicyBuilder = (deviceType, baselineType = null) => {
    setPolicyModal({ isOpen: true, deviceType, baselineType });
  };

  const closePolicyBuilder = () => {
    setPolicyModal({ isOpen: false, deviceType: null, baselineType: null });
  };

  const handlePolicySave = (policy) => {
    if (!policy?.deviceType) {
      closePolicyBuilder();
      return;
    }
    setCustomPolicies((prev) => {
      const nextState = { ...prev };
      if (policy.baselineType) {
        const baselineBucket = nextState.baselines?.[policy.baselineType] || {};
        const devicePolicies = baselineBucket[policy.deviceType] || [];
        const updatedBaseline = {
          ...baselineBucket,
          [policy.deviceType]: [...devicePolicies, policy]
        };
        nextState.baselines = {
          ...(nextState.baselines || {}),
          [policy.baselineType]: updatedBaseline
        };
      } else {
        const globalBucket = nextState.general?.[policy.deviceType] || [];
        nextState.general = {
          ...(nextState.general || {}),
          [policy.deviceType]: [...globalBucket, policy]
        };
      }
      return nextState;
    });
    closePolicyBuilder();
    setNotification({
      title: 'Policy Saved',
      msg: `${policy.title} is ready for deployment.`,
      type: 'success'
    });
    setTimeout(() => setNotification(null), 3000);
  };
  
  const confirmDeployment = async (targetInfo) => {
    setModalOpen(false);
    
    // targetInfo is structured object now
    const { targetType, targetId, params, description } = targetInfo;
    
    // Get device type from script if available
    const scriptDeviceType = selectedScript?.deviceType;
    
    setNotification({ title: 'Deployment Queued', msg: `Script '${selectedScript.title}' deploying to ${description}...`, type: 'success' });
    
    try {
        // Determine targets
        let targetDeviceIds = [];
        if (targetType === 'single') {
            targetDeviceIds = [targetId];
        } else if (targetType === 'multiple') {
            targetDeviceIds = targetId.split(',').map(s => s.trim()).filter(Boolean);
        } else if (targetType === 'collection') {
            // Fetch devices in group or filter locally
            if (targetId.startsWith('plat-')) {
                // Platform collection
                targetDeviceIds = devices.filter(d => deviceMatchesOS(d, targetId)).map(d => d.id);
            } else if (targetId.startsWith('dept-') || targetId === 'Unassigned') {
                // Department collection
                targetDeviceIds = devices.filter(d => d.dept === targetId).map(d => d.id);
            } else {
                // Fallback: try to match by any property
                targetDeviceIds = devices.filter(d => 
                    d.platform === targetId || 
                    d.dept === targetId ||
                    d.id === targetId
                ).map(d => d.id);
                if (targetDeviceIds.length === 0) {
                    targetDeviceIds = devices.map(d => d.id); // Fallback all
                }
            }
        } else {
            // Context aware (preSelectedTarget)
             if (selectedCount > 0) {
                 targetDeviceIds = Object.keys(checkedDevices).filter(k => checkedDevices[k]);
             } else {
                 targetDeviceIds = filteredDevices.map(d => d.id);
             }
        }

        // Filter by device type if script has a specific device type
        if (scriptDeviceType) {
          const platformMap = {
            'windows': 'plat-windows',
            'android': 'plat-android',
            'macos': 'plat-macos',
            'linux': 'plat-linux',
            'ios': 'plat-ios'
          };
          const requiredPlatform = platformMap[scriptDeviceType];
          
          if (requiredPlatform) {
            // Filter to only compatible devices
            const compatibleDevices = devices.filter(d => deviceMatchesOS(d, requiredPlatform));
            const compatibleDeviceIds = compatibleDevices.map(d => d.id);
            
            // Store original count for warning message
            const originalCount = targetDeviceIds.length;
            
            // Only keep device IDs that match the required platform
            targetDeviceIds = targetDeviceIds.filter(id => {
              const device = devices.find(d => d.id === id);
              return device && deviceMatchesOS(device, requiredPlatform);
            });
            
            if (targetDeviceIds.length === 0) {
              setNotification({ 
                title: 'No Compatible Devices', 
                msg: `This ${scriptDeviceType.toUpperCase()} policy cannot be deployed. No ${scriptDeviceType} devices found in the selected target.`, 
                type: 'error' 
              });
              setTimeout(() => setNotification(null), 5000);
              return;
            }
            
            // Warn if some devices were filtered out
            const filteredCount = originalCount - targetDeviceIds.length;
            if (filteredCount > 0) {
              setNotification({ 
                title: 'Deployment Filtered', 
                msg: `Deploying to ${targetDeviceIds.length} ${scriptDeviceType.toUpperCase()} device(s). ${filteredCount} incompatible device(s) excluded.`, 
                type: 'success' 
              });
            }
          }
        }

        // Execute for each device (route to correct backend based on device type)
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        for (const deviceId of targetDeviceIds) {
            try {
                // Find device by device_id or id field
                const device = devices.find(d => (d.device_id || d.id) === deviceId);
                const devicePlatform = device?.platform || '';
                
                // Route based on script device type
                if (scriptDeviceType === 'android') {
                    // Android policies - route to Buntu MDM backend
                    const policyId = selectedScript.id;
                    
                    if (policyId === 'android-wipe') {
                        await apiService.wipeAndroidDevice(deviceId, false);
                    } else if (policyId === 'android-lock') {
                        await apiService.lockAndroidDevice(deviceId);
                    } else if (policyId === 'android-create-profile' || policyId === 'android-push-profile') {
                        await apiService.syncAndroidPolicy(deviceId);
                    } else if (policyId === 'android-sync-policy') {
                        await apiService.syncAndroidPolicy(deviceId);
                    } else {
                        // Generic Android command - use MDM command queue
                        await apiService.queueBuntuMDMCommand(deviceId, 'AndroidCommand', {
                            action: policyId,
                            payload: params
                        });
                    }
                    successCount++;
                } else if (scriptDeviceType === 'ios') {
                    // iOS policies - route to Buntu MDM backend
                    const policyId = selectedScript.id;
                    let mdmCommandType = 'DeviceInformation';
                    
                    if (policyId === 'ios-wipe' || policyId === 'ios-erase') {
                        mdmCommandType = 'EraseDevice';
                    } else if (policyId === 'ios-lock') {
                        mdmCommandType = 'DeviceLock';
                    } else if (policyId === 'ios-install-profile') {
                        mdmCommandType = 'InstallProfile';
                    } else if (policyId === 'ios-remove-profile') {
                        mdmCommandType = 'RemoveProfile';
                    } else if (policyId === 'ios-device-info') {
                        mdmCommandType = 'DeviceInformation';
                    } else if (policyId === 'ios-restrictions') {
                        mdmCommandType = 'InstallProfile'; // Restrictions are installed as profiles
                    }
                    
                    await apiService.queueBuntuMDMCommand(deviceId, mdmCommandType, params);
                    successCount++;
                } else {
                    // Windows/Linux/macOS - route to Go Agent backend
                    // Determine command type based on script language
                    let commandType = 'powershell';
                    if (selectedScript.lang === 'Bash' || selectedScript.lang === 'Shell') {
                        commandType = 'bash';
                    } else if (selectedScript.lang === 'PowerShell') {
                        commandType = 'powershell';
                    } else if (selectedScript.lang) {
                        commandType = selectedScript.lang.toLowerCase();
                    }
                    
                    await apiService.executeGoAgentCommand(deviceId, {
                        command_type: commandType,
                        command: selectedScript.content || '# No content',
                        parameters: params,
                        timeout_seconds: 600,
                        created_by: 'buntu-dashboard'
                    });
                    successCount++;
                }
            } catch (e) {
                errorCount++;
                const device = devices.find(d => (d.device_id || d.id) === deviceId);
                const deviceName = device?.name || device?.device_id || deviceId;
                errors.push(`${deviceName}: ${e.message || 'Unknown error'}`);
                console.error(`Failed to deploy to ${deviceId}`, e);
            }
        }
        
        // Show notification with results
        if (errorCount === 0) {
            setNotification({ 
                title: 'Deployment Initiated', 
                msg: `Command sent to ${successCount} device(s).`, 
                type: 'success' 
            });
        } else if (successCount > 0) {
            setNotification({ 
                title: 'Partial Deployment', 
                msg: `Sent to ${successCount} device(s). ${errorCount} failed. ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`, 
                type: 'error' 
            });
        } else {
            setNotification({ 
                title: 'Deployment Failed', 
                msg: `Failed to deploy to all devices. ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`, 
                type: 'error' 
            });
        }
        
    } catch (err) {
        console.error(err);
        setNotification({ title: 'Deployment Failed', msg: 'Error initiating deployment.', type: 'error' });
    }
    
    setTimeout(() => setNotification(null), 4000);
    setCheckedDevices({});
  };

  const toggleDevice = (id) => setCheckedDevices(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    if (selectedCount === filteredDevices.length) setCheckedDevices({});
    else { const newChecked = {}; filteredDevices.forEach(d => newChecked[d.id] = true); setCheckedDevices(newChecked); }
  };

  const navigateToPlatform = (platId, name) => {
    setSelectedNode({ id: platId, name: name, type: 'platform', category: 'inventory' });
    setViewMode('devices');
  };

  // --- Dynamic Navigation Tree ---
  const deviceGroups = useMemo(() => {
    // Helper to format ID to Name
    const formatName = (id, prefix) => {
      return id.replace(prefix, '').split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
    };

    // Extract Platforms
    const platforms = Array.from(new Set(devices.map(d => d.platform))).map(p => ({
      id: p,
      name: formatName(p, 'plat-'),
      type: 'platform'
    }));
    
    // Ensure common platforms exist in tree even if empty (optional, but good for UI consistency)
    const commonPlatforms = ['plat-windows', 'plat-linux', 'plat-macos', 'plat-android', 'plat-ios'];
    commonPlatforms.forEach(p => {
      if (!platforms.find(ex => ex.id === p)) {
        platforms.push({ id: p, name: formatName(p, 'plat-'), type: 'platform' });
      }
    });

    // Extract Departments from devices
    const deviceDepts = Array.from(new Set(devices.map(d => d.dept))).map(d => {
      if (!d) return null;
      
      // Check if this is a custom collection and use its stored name
      const customCollection = customCollections.find(c => c.id === d);
      if (customCollection) {
        return {
          id: d,
          name: customCollection.name,
          type: 'dept',
          isCustom: true
        };
      }
      
      // If it's a custom collection ID but missing from localStorage, provide better fallback
      if (d.startsWith('dept-custom-')) {
        // For missing custom collections, use a generic name instead of formatted ID
        // Users can rename it later by clicking on the collection
        return {
          id: d,
          name: 'Unnamed Collection', // Better than "Custom 1764593995400 N0coa2lv8"
          type: 'dept',
          isCustom: true,
          needsName: true // Flag to indicate this needs a proper name
        };
      }
      
      // For standard departments, format the ID
      return {
        id: d,
        name: d === 'Unassigned' ? 'Unassigned' : formatName(d, 'dept-'),
        type: 'dept'
      };
    }).filter(d => d !== null);
    
    // Common departments that should always be available
    const commonDepartments = [
      { id: 'dept-engineering', name: 'Engineering', type: 'dept' },
      { id: 'dept-sales', name: 'Sales', type: 'dept' },
      { id: 'dept-it', name: 'IT', type: 'dept' },
      { id: 'dept-hr', name: 'HR', type: 'dept' },
      { id: 'dept-finance', name: 'Finance', type: 'dept' },
      { id: 'dept-marketing', name: 'Marketing', type: 'dept' },
      { id: 'dept-operations', name: 'Operations', type: 'dept' },
      { id: 'dept-support', name: 'Support', type: 'dept' }
    ];
    
    // Default "Departments" folder ID
    const departmentsFolderId = 'folder-departments-default';
    const departmentsFolder = customFolders.find(f => f.id === departmentsFolderId);
    
    // Merge device departments with common departments, avoiding duplicates
    const deptMap = new Map();
    deviceDepts.forEach(dept => {
      deptMap.set(dept.id, {
        ...dept,
        folderId: null // Device departments start without folder assignment
      });
    });
    
    // Add common departments to the default "Departments" folder
    commonDepartments.forEach(dept => {
      if (!deptMap.has(dept.id)) {
        deptMap.set(dept.id, {
          ...dept,
          folderId: departmentsFolderId // Assign to default Departments folder
        });
      } else {
        // If department exists from devices but is a common department, assign to default folder
        const existing = deptMap.get(dept.id);
        if (commonDepartments.find(cd => cd.id === dept.id) && !existing.isCustom) {
          existing.folderId = departmentsFolderId;
        }
      }
    });
    
    // Add custom collections
    customCollections.forEach(collection => {
      if (!deptMap.has(collection.id)) {
        deptMap.set(collection.id, {
          id: collection.id,
          name: collection.name,
          type: 'dept',
          isCustom: true,
          folderId: collection.folderId || null
        });
      }
    });
    
    // Organize collections by folders
    const collectionsInFolders = {};
    const collectionsWithoutFolders = [];
    
    Array.from(deptMap.values()).forEach(dept => {
      if (dept.folderId) {
        if (!collectionsInFolders[dept.folderId]) {
          collectionsInFolders[dept.folderId] = [];
        }
        collectionsInFolders[dept.folderId].push(dept);
      } else {
        collectionsWithoutFolders.push(dept);
      }
    });
    
    // Sort collections without folders
    collectionsWithoutFolders.sort((a, b) => {
      if (a.id === 'Unassigned') return 1;
      if (b.id === 'Unassigned') return -1;
      return a.name.localeCompare(b.name);
    });
    
    // Build folder structure
    const folderNodes = customFolders.map(folder => ({
      id: folder.id,
      name: folder.name,
      type: 'folder',
      isDefault: folder.isDefault || false,
      children: (collectionsInFolders[folder.id] || []).sort((a, b) => a.name.localeCompare(b.name))
    }));
    
    // Combine folders and collections without folders
    // Put "Departments" folder first, then other folders, then root-level collections
    const departmentsFolderNode = folderNodes.find(f => f.id === departmentsFolderId);
    const otherFolders = folderNodes.filter(f => f.id !== departmentsFolderId);
    const depts = [
      ...(departmentsFolderNode ? [departmentsFolderNode] : []),
      ...otherFolders,
      ...collectionsWithoutFolders
    ];

    return [
      {
        id: 'root',
        name: 'All Devices',
        type: 'root',
        category: 'inventory',
        children: [
          {
            id: 'cat-platforms',
            name: 'By Platform',
            type: 'category',
            children: platforms
          },
          {
            id: 'cat-depts',
            name: 'By Groups',
            type: 'category',
            children: depts
          }
        ]
      }
    ];
  }, [devices, customCollections, customFolders]);

  return (
    <div className={embedded ? 'flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50' : 'flex h-screen w-full bg-slate-100 text-slate-800 font-sans overflow-hidden'}>
      {!embedded && (
      <>
      {/* --- Sidebar --- */}
      <div className="w-64 flex-shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col text-slate-300">
        <div className="h-16 flex items-center px-4 border-b border-slate-800">
          {logoError ? (
            <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white font-bold mr-3 shadow-lg shadow-blue-900/50 shrink-0">
              B
            </div>
          ) : (
            <img 
              src={`${process.env.PUBLIC_URL || ''}/Buntu.png`} 
              alt="Buntu Logo" 
              className="w-8 h-8 rounded mr-3 shadow-lg shrink-0 object-contain bg-slate-800" 
              style={{ maxWidth: '32px', maxHeight: '32px' }}
              onError={() => setLogoError(true)}
            />
          )}
          <div className="min-w-0">
            <span className="font-bold text-lg tracking-tight text-white block leading-none">Buntu</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold block truncate">Unified Endpoint Management</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4">

          {/* Dashboard Home Link */}
          <div
            onClick={() => setSelectedNode({ id: 'dashboard', name: t('common.overview'), type: 'dashboard' })}
            className={`mx-3 mb-6 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'dashboard' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            <LayoutDashboard size={18} className="mr-3" />
            <span className="font-medium text-sm">{t('common.overview')}</span>
          </div>

          <div className="px-4 mb-2 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('navigation.deviceGroups')}</span>
          </div>
          <div className="space-y-0.5 mb-6">
            {deviceGroups.map(node => (
              <TreeNode key={node.id} node={node} level={0} selectedId={selectedNode.id} onSelect={(n) => { setSelectedNode(n); setCheckedDevices({}); if (n.id !== 'go-agent-management') { setViewMode('devices'); } }} />
            ))}
          </div>

          <div className="px-4 mb-2 mt-6 border-t border-slate-800 pt-6 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('navigation.baselines')}</span>
          </div>
          <div className="space-y-0.5 mb-6">
            {baselines.map(node => (
              <TreeNode key={node.id} node={node} level={0} selectedId={selectedNode.id} onSelect={(n) => { setSelectedNode(n); setViewMode('scripts'); }} />
            ))}
          </div>

          <div className="px-4 mb-2 mt-6 border-t border-slate-800 pt-6 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('navigation.applications')}</span>
          </div>
          <div className="space-y-0.5 mb-6">
            <div
              className={`mx-3 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'application-deployment' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
              onClick={() => setSelectedNode({ id: 'application-deployment', name: 'Application Deployment', type: 'application' })}
            >
              <Box size={18} className="mr-3" />
              <span className="font-medium text-sm">Application Deployment</span>
            </div>
            <div
              className={`mx-3 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'go-agent-management' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
              onClick={() => setSelectedNode({ id: 'go-agent-management', name: 'Go Agent Management', type: 'management' })}
            >
              <Laptop size={18} className="mr-3" />
              <span className="font-medium text-sm">Go Agent Management</span>
            </div>
            <div
              className={`mx-3 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'patch-management' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
              onClick={() => setSelectedNode({ id: 'patch-management', name: 'Patch Management', type: 'application' })}
            >
              <ShieldCheck size={18} className="mr-3" />
              <span className="font-medium text-sm">Patch Management</span>
            </div>
            <div
              className={`mx-3 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'license-management' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
              onClick={() => setSelectedNode({ id: 'license-management', name: 'License Management', type: 'application' })}
            >
              <FileText size={18} className="mr-3" />
              <span className="font-medium text-sm">License Management</span>
            </div>
            <div
              className={`mx-3 flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors ${selectedNode.id === 'client-settings' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
              onClick={() => setSelectedNode({ id: 'client-settings', name: 'Client Settings', type: 'application' })}
            >
              <SettingsIcon size={18} className="mr-3" />
              <span className="font-medium text-sm">Client Settings</span>
            </div>
          </div>

          <div className="px-4 mb-2 mt-6 border-t border-slate-800 pt-6 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('navigation.deviceAnalytics')}</span>
          </div>
          <div className="space-y-0.5 mb-6">
            {analyticsGroups.map(node => (
              <TreeNode key={node.id} node={node} level={0} selectedId={selectedNode.id} onSelect={(n) => { setSelectedNode(n); setCheckedDevices({}); }} />
            ))}
          </div>

        </div>
        <div className="p-4 border-t border-slate-800 bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs">A</div>
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Admin User</div>
              <div className="text-xs text-slate-500">Global Admin</div>
            </div>
            <SettingsIcon 
              size={16} 
              className="text-slate-500 hover:text-white cursor-pointer" 
              title={onNavigateToSettings ? 'Open Settings (includes Osquery)' : 'Settings'}
              onClick={() => (onNavigateToSettings ? onNavigateToSettings() : setSettingsModalOpen(true))}
            />
          </div>
        </div>
      </div>
      </>
      )}

      {/* --- Main Content Area --- */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50">

        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 shadow-sm z-10">
          <div className="flex flex-col">
            <div className="flex items-center text-xs text-slate-500 space-x-2 mb-0.5">
              <span>{isDashboard ? t('common.dashboard') : isBaselineView ? t('navigation.baselineLibrary', 'Baseline Library') : isAnalyticsView ? t('navigation.deviceAnalytics') : t('common.inventory', 'Inventory')}</span>
              <ChevronRight size={10} />
              <span className="uppercase">{selectedNode.name}</span>
            </div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              {selectedNode.id === 'client-settings' && <SettingsIcon size={20} className="text-blue-600" />}
              {selectedNode.name}
              {!isBaselineView && !isDashboard && !isAnalyticsView && selectedNode.id !== 'application-deployment' && selectedNode.id !== 'client-settings' && (
                <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full font-medium border border-slate-200">
                  {filteredDevices.length}
                </span>
              )}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {!isDashboard && !isAnalyticsView && (
              <>
                {!isBaselineView && selectedCount > 0 && (
                  <>
                    <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-100 animate-in fade-in slide-in-from-top-2">
                      <CheckCircle size={14} />
                      <span className="text-sm font-semibold">{selectedCount} {t('common.selected', 'Selected')}</span>
                      <button onClick={() => setCheckedDevices({})} className="ml-2 hover:bg-blue-100 p-0.5 rounded"><X size={12} /></button>
                    </div>
                    <button
                      onClick={() => {
                        const selectedDeviceIds = Object.keys(checkedDevices).filter(id => checkedDevices[id]);
                        const selectedDevices = devices.filter(d => selectedDeviceIds.includes(d.id));
                        setMoveModal({ 
                          isOpen: true, 
                          deviceIds: selectedDeviceIds, 
                          deviceNames: selectedDevices.map(d => d.name) 
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <Move size={16} />
                      {t('common.moveSelected', 'Move Selected')}
                    </button>
                  </>
                )}
                {!isBaselineView && selectedCount > 0 && <div className="h-8 w-px bg-slate-200 mx-2"></div>}
                {isBaselineView ? (
                  <button
                    onClick={() => setViewMode('adhoc')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-colors ${
                      viewMode === 'adhoc' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Terminal size={16} /> {t('common.runCustomScript', 'Run Custom Script')}
                  </button>
                ) : selectedNode.id !== 'cat-platforms' && selectedNode.id !== 'application-deployment' && selectedNode.id !== 'go-agent-management' && selectedNode.id !== 'patch-management' && selectedNode.id !== 'license-management' ? (
                  <>
                    <button onClick={() => setViewMode('devices')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'devices' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                      <Monitor size={16} /> {t('common.devices')}
                    </button>
                    <button onClick={() => setViewMode('scripts')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'scripts' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                      <Terminal size={16} /> {t('common.quickActions', 'Quick Actions')}
                    </button>
                  </>
                ) : null}
              </>
            )}
            <LanguageSelector />
            <div className="h-8 w-px bg-slate-200 mx-1" />
            <span className="hidden sm:inline text-sm text-slate-500 truncate max-w-[140px]">
              {user?.full_name || user?.username || ''}
            </span>
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </header>

        {/* Canvas */}
        <main className="flex-1 overflow-auto p-6">

          {/* --- DASHBOARD VIEW --- */}
          {isDashboard && (
            <div className="space-y-8 animate-in fade-in duration-500">
              {/* Top Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Backend API Card */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4 overflow-hidden">
                  <div className={`p-3 rounded-lg shrink-0 ${dashboardStats ? 'bg-indigo-50 text-indigo-600' : 'bg-red-50 text-red-600'}`}><Globe size={24} /></div>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-500 font-medium truncate">{t('dashboard.apiStatus', 'API Status')}</p>
                    <div className="flex items-center gap-2">
                      <p className={`text-lg font-bold truncate ${dashboardStats ? 'text-green-600' : loading ? 'text-yellow-600' : 'text-red-600'}`}>
                        {loading ? t('dashboard.connecting', 'Connecting...') : dashboardStats ? t('dashboard.connected', 'Connected') : t('common.offline')}
                      </p>
                      {dashboardStats && <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse shrink-0"></span>}
                    </div>
                    <p className="text-xs text-slate-400 truncate">{loading ? t('dashboard.checking', 'Checking...') : dashboardStats ? (dashboardStats.latency ? `${dashboardStats.latency}ms ${t('dashboard.latency', 'Latency')}` : t('common.online')) : t('dashboard.connectionFailed', 'Connection Failed')}</p>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><Server size={24} /></div>
                  <div><p className="text-sm text-slate-500 font-medium">{t('dashboard.totalAgents', 'Total Agents')}</p><p className="text-2xl font-bold text-slate-800">{devices.length}</p></div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="p-3 bg-green-50 text-green-600 rounded-lg"><Activity size={24} /></div>
                  <div><p className="text-sm text-slate-500 font-medium">{t('common.online')}</p><p className="text-2xl font-bold text-slate-800">{devices.filter(d => isDeviceOnline(d.last_seen)).length}</p></div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg"><AlertCircle size={24} /></div>
                  <div><p className="text-sm text-slate-500 font-medium">{t('dashboard.critical', 'Critical')}</p><p className="text-2xl font-bold text-slate-800">{devices.filter(d => getDeviceHealth(d.last_seen) === 'critical').length}</p></div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
                  <div className="p-3 bg-slate-50 text-slate-600 rounded-lg"><WifiOff size={24} /></div>
                  <div><p className="text-sm text-slate-500 font-medium">{t('common.offline')}</p><p className="text-2xl font-bold text-slate-800">{devices.filter(d => !isDeviceOnline(d.last_seen)).length}</p></div>
                </div>
              </div>

              {/* OS Platform Cards */}
              <div>
                <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2"><LayoutDashboard size={18} /> {t('dashboard.platformOverview', 'Platform Overview')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <DashboardCard
                    iconClass="fab fa-windows"
                    title={t('platforms.windows', 'Windows')}
                    color="bg-blue-500"
                    count={getPlatformStats('plat-windows').total}
                    subtext={`${getPlatformStats('plat-windows').online} ${t('common.active')}`}
                    onClick={() => navigateToPlatform('plat-windows', t('platforms.windows', 'Windows'))}
                  />
                  <DashboardCard
                    iconClass="fab fa-linux"
                    title={t('platforms.linux', 'Linux')}
                    color="bg-orange-500"
                    count={getPlatformStats('plat-linux').total}
                    subtext={`${getPlatformStats('plat-linux').online} ${t('common.active')}`}
                    onClick={() => navigateToPlatform('plat-linux', t('platforms.linux', 'Linux'))}
                  />
                  <DashboardCard
                    iconClass="fab fa-apple"
                    title={t('platforms.macos', 'macOS')}
                    color="bg-slate-500"
                    count={getPlatformStats('plat-macos').total}
                    subtext={`${getPlatformStats('plat-macos').online} ${t('common.active')}`}
                    onClick={() => navigateToPlatform('plat-macos', t('platforms.macos', 'macOS'))}
                  />
                  <DashboardCard
                    iconClass="fab fa-android"
                    title={t('platforms.android', 'Android')}
                    color="bg-green-500"
                    count={getPlatformStats('plat-android').total}
                    subtext={`${getPlatformStats('plat-android').online} ${t('common.active')}`}
                    onClick={() => navigateToPlatform('plat-android', t('platforms.android', 'Android'))}
                  />
                  <DashboardCard
                    iconClass="fab fa-app-store-ios"
                    title={t('platforms.ios', 'iOS')}
                    color="bg-gray-400"
                    count={getPlatformStats('plat-ios').total}
                    subtext={`${getPlatformStats('plat-ios').online} ${t('common.active')}`}
                    onClick={() => navigateToPlatform('plat-ios', t('platforms.ios', 'iOS'))}
                  />
                </div>
              </div>

              {/* Health Honeycomb - Dynatrace-style: card per group, hex per device */}
              <HealthHoneycombView
                devices={devices}
                collections={customCollections}
                folders={customFolders}
                complianceData={complianceData}
                backendConnected={dashboardStats != null}
                hasRealMetrics={devices.some((d) => (d.cpu ?? 0) > 0 || (d.ram ?? 0) > 0 || (d.storage ?? 0) > 0 || (d.last_seen && (Date.now() - new Date(d.last_seen).getTime() < 5 * 60 * 1000)))}
                metricsFromOsquery={devices.some((d) => d.has_osquery)}
                onCellClick={(g) => {
                  if (g && g.id && g.id.startsWith('plat-')) {
                    setSelectedNode({ id: 'cat-platforms', name: 'By Platform', type: 'category' });
                  } else if (g && g.id) {
                    setSelectedNode({ id: g.id, name: g.name, type: 'dept' });
                  }
                }}
                onDeviceClick={(device, group) => {
                  if (group?.id?.startsWith('plat-')) {
                    setSelectedNode({ id: 'cat-platforms', name: 'By Platform', type: 'category' });
                  } else if (group) {
                    setSelectedNode({ id: group.id, name: group.name, type: 'dept' });
                  }
                }}
              />

              {/* Recent Alerts Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-700 mb-4">{t('dashboard.recentAlerts', 'Recent Alerts')}</h2>
                <div className="space-y-3">
                  {devices.filter(d => getDeviceHealth(d.last_seen) === 'critical').length === 0 && devices.filter(d => !isDeviceOnline(d.last_seen)).length === 0 && (
                     <div className="text-sm text-slate-500 italic">{t('dashboard.noAlerts', 'No critical alerts. System is healthy.')}</div>
                  )}
                  {devices.filter(d => getDeviceHealth(d.last_seen) === 'critical' || !isDeviceOnline(d.last_seen)).slice(0, 5).map(d => {
                    const isOffline = !isDeviceOnline(d.last_seen);
                    const isCritical = getDeviceHealth(d.last_seen) === 'critical';
                    let msg = isCritical ? t('dashboard.criticalStatus', 'Critical Status') + ' — ' + formatRelativeTime(d.last_seen) : isOffline ? formatRelativeTime(d.last_seen) : t('dashboard.criticalStatus', 'Critical Status');
                    if (!isOffline && !isCritical) {
                        if (d.cpu > 80) msg = t('dashboard.highCpu', 'High CPU Usage', { cpu: d.cpu });
                        else if (d.ram > 80) msg = t('dashboard.highMemory', 'High Memory Usage', { ram: d.ram });
                    }
                    
                    return (
                        <div key={d.id} className={`flex items-center justify-between p-3 border rounded-lg ${isOffline ? 'bg-slate-50 border-slate-200' : 'bg-red-50 border-red-100'}`}>
                          <div className="flex items-center gap-3">
                            {isOffline ? <WifiOff size={18} className="text-slate-400" /> : <AlertCircle size={18} className="text-red-500" />}
                            <span className={`text-sm font-medium ${isOffline ? 'text-slate-600' : 'text-red-700'}`}>{msg} on {d.name}</span>
                          </div>
                          <button className={`text-xs bg-white border px-3 py-1 rounded ${isOffline ? 'border-slate-200 text-slate-500' : 'border-red-200 text-red-600 hover:bg-red-50'}`}>View</button>
                        </div>
                    );
                  })}
                  {devices.filter(d => isDeviceOnline(d.last_seen)).length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-100 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-green-500" />
                        <span className="text-sm font-medium text-green-700">System Health Check Passed on {devices.filter(d => isDeviceOnline(d.last_seen)).length} Devices</span>
                      </div>
                      <span className="text-xs text-green-600">Live</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* --- FOLDER VIEW (When folder is selected) --- */}
          {!isDashboard && !isBaselineView && selectedNode.type === 'folder' && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-300 mb-6">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-purple-50/30">
                <div className="flex items-center gap-3">
                  <Folder size={24} className="text-purple-600" />
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{selectedNode.name}</h3>
                    <p className="text-sm text-slate-500">
                      {selectedNode.children?.length || 0} collections
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    const name = window.prompt('Enter collection name:');
                    if (name && name.trim()) {
                      const newDeptId = `dept-custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                      const newCollection = {
                        id: newDeptId,
                        name: name.trim(),
                        folderId: selectedNode.id,
                        createdAt: new Date().toISOString(),
                        criticalDeviceIds: [],
                      };
                      const updatedCollections = [...customCollections, newCollection];
                      saveCustomCollections(updatedCollections);
                      setNotification({ 
                        title: 'Collection Created', 
                        msg: `Collection "${name}" has been created in "${selectedNode.name}".`, 
                        type: 'success' 
                      });
                      setTimeout(() => setNotification(null), 3000);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus size={16} />
                  Create Collection in Folder
                </button>
              </div>
              <div className="p-4">
                {selectedNode.children && selectedNode.children.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedNode.children.map(collection => {
                      const isCustom = collection.isCustom || customCollections.some(c => c.id === collection.id);
                      return (
                        <div key={collection.id} className="border-2 border-blue-300 rounded-lg p-4 hover:shadow-md transition-shadow bg-blue-50/30">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Briefcase size={18} className="text-blue-600 flex-shrink-0" />
                              <h4 className="font-semibold text-slate-800 truncate">{collection.name}</h4>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              <button
                                onClick={() => {
                                  const newName = window.prompt('Enter new name:', collection.name);
                                  if (newName && newName.trim() && newName !== collection.name) {
                                    if (isCustom) {
                                      const updated = customCollections.map(c => 
                                        c.id === collection.id ? { ...c, name: newName.trim() } : c
                                      );
                                      saveCustomCollections(updated);
                                    } else {
                                      // For default collections, we can't rename them directly
                                      // But we could create a custom collection with the new name
                                      window.alert('Default collections cannot be renamed. Please create a custom collection if you need a different name.');
                                      return;
                                    }
                                    setNotification({ 
                                      title: 'Collection Updated', 
                                      msg: `Collection renamed to "${newName}".`, 
                                      type: 'success' 
                                    });
                                    setTimeout(() => setNotification(null), 3000);
                                  }
                                }}
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit Collection"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  // Get available folders and root option
                                  const availableFolders = customFolders.filter(f => f.id !== selectedNode.id);
                                  const options = ['(Root Level)', ...availableFolders.map(f => f.name)];
                                  const optionText = options.map((opt, idx) => `${idx}. ${opt}`).join('\n');
                                  const choice = window.prompt(
                                    `Move "${collection.name}" to:\n\n${optionText}\n\nEnter number (or cancel):`
                                  );
                                  if (choice !== null) {
                                    const index = parseInt(choice);
                                    if (index === 0) {
                                      // Move to root
                                      if (isCustom) {
                                        const updated = customCollections.map(c => 
                                          c.id === collection.id ? { ...c, folderId: null } : c
                                        );
                                        saveCustomCollections(updated);
                                      } else {
                                        // For default collections, we need to handle differently
                                        // We'll create a custom collection at root with same name
                                        const newCollection = {
                                          id: `dept-custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                          name: collection.name,
                                          folderId: null,
                                          createdAt: new Date().toISOString()
                                        };
                                        const updated = [...customCollections, newCollection];
                                        saveCustomCollections(updated);
                                      }
                                      setNotification({ 
                                        title: 'Collection Moved', 
                                        msg: `"${collection.name}" moved to root level.`, 
                                        type: 'success' 
                                      });
                                    } else if (index > 0 && index <= availableFolders.length) {
                                      // Move to another folder
                                      const targetFolder = availableFolders[index - 1];
                                      if (isCustom) {
                                        const updated = customCollections.map(c => 
                                          c.id === collection.id ? { ...c, folderId: targetFolder.id } : c
                                        );
                                        saveCustomCollections(updated);
                                      } else {
                                        // For default collections, create a custom collection in the target folder
                                        const newCollection = {
                                          id: `dept-custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                          name: collection.name,
                                          folderId: targetFolder.id,
                                          createdAt: new Date().toISOString()
                                        };
                                        const updated = [...customCollections, newCollection];
                                        saveCustomCollections(updated);
                                      }
                                      setNotification({ 
                                        title: 'Collection Moved', 
                                        msg: `"${collection.name}" moved to "${targetFolder.name}".`, 
                                        type: 'success' 
                                      });
                                    }
                                    setTimeout(() => setNotification(null), 3000);
                                  }
                                }}
                                className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="Move Collection"
                              >
                                <Move size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  if (isCustom) {
                                    if (window.confirm(`Are you sure you want to delete the "${collection.name}" collection? This will not delete the devices, only the collection grouping.`)) {
                                      const updated = customCollections.filter(c => c.id !== collection.id);
                                      saveCustomCollections(updated);
                                      setNotification({ 
                                        title: 'Collection Deleted', 
                                        msg: `"${collection.name}" has been deleted.`, 
                                        type: 'success' 
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  } else {
                                    window.alert('Default collections cannot be deleted. You can only delete custom collections.');
                                  }
                                }}
                                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete Collection"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="text-sm text-slate-600 mb-3">
                            <span className="font-medium">{devices.filter(d => d.dept === collection.id).length}</span> devices
                          </div>
                          <button
                            onClick={() => setSelectedNode(collection)}
                            className="w-full text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-2 rounded border border-blue-200 transition-colors"
                          >
                            View Collection
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Folder size={48} className="mx-auto mb-3 text-slate-300" />
                    <p>No collections in this folder yet.</p>
                    <p className="text-sm mt-1">Click "Create Collection in Folder" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Note: Device information is NOT shown in folder view - folders only contain collections */}

          {/* --- COLLECTION VIEW (When collection is selected) --- */}
          {!isDashboard && !isBaselineView && selectedNode.type === 'dept' && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-300 mb-6">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-blue-50/30">
                <div className="flex items-center gap-3">
                  <Briefcase size={24} className="text-blue-600" />
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{selectedNode.name}</h3>
                    <p className="text-sm text-slate-500">
                      {filteredDevices.length} devices in this collection
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => {
                      setDeviceImportModal({
                        isOpen: true,
                        collectionId: selectedNode.id,
                        collectionName: selectedNode.name,
                        importMode: 'single'
                      });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <Plus size={16} />
                    Add Devices
                  </button>
                  {selectedCount > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const selectedIds = Object.keys(checkedDevices).filter((id) => checkedDevices[id]);
                          setDevicesCriticalInCollection(
                            selectedNode.id,
                            selectedNode.name,
                            selectedIds,
                            true,
                            selectedNode.folderId ?? null
                          );
                          setCheckedDevices({});
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium"
                        title="Mark selected devices as critical for honeycomb cards"
                      >
                        <AlertTriangle size={16} />
                        Mark Critical ({selectedCount})
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const selectedIds = Object.keys(checkedDevices).filter((id) => checkedDevices[id]);
                          setDevicesCriticalInCollection(
                            selectedNode.id,
                            selectedNode.name,
                            selectedIds,
                            false,
                            selectedNode.folderId ?? null
                          );
                          setCheckedDevices({});
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
                        title="Remove critical mark from selected devices"
                      >
                        <Flag size={16} />
                        Unmark Critical
                      </button>
                    </>
                  )}
                  {filteredDevices.length > 0 && (
                    <>
                      <button
                        onClick={() => {
                          // Export to CSV/Excel
                          const headers = ['Device ID', 'Name', 'OS', 'Platform', 'Status', 'IP Address', 'Department', 'Critical'];
                          let content = headers.join(',') + '\n';
                          filteredDevices.forEach(device => {
                            const row = [
                              device.id || device.device_id || '',
                              device.name || '',
                              device.os || '',
                              device.platform || '',
                              device.status || '',
                              device.ip || '',
                              device.dept || '',
                              isDeviceCriticalInCollection(selectedNode.id, device.id || device.device_id) ? 'Yes' : 'No'
                            ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
                            content += row + '\n';
                          });
                          const blob = new Blob([content], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${selectedNode.name}_${new Date().toISOString().split('T')[0]}.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <FileSpreadsheet size={16} />
                        Export
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete all devices from "${selectedNode.name}" collection? This will remove devices from the collection but will not delete the devices themselves.`)) {
                            // Remove all devices from this collection by updating their dept
                            const updatedDevices = devices.map(d => 
                              d.dept === selectedNode.id ? { ...d, dept: 'Unassigned' } : d
                            );
                            setDevices(updatedDevices);
                            // Clear critical marks for this collection
                            const col = customCollections.find((c) => c.id === selectedNode.id);
                            if (col) {
                              saveCustomCollections(
                                customCollections.map((c) =>
                                  c.id === selectedNode.id ? { ...c, criticalDeviceIds: [] } : c
                                )
                              );
                            }
                            setNotification({ 
                              title: 'Devices Removed', 
                              msg: `All devices have been removed from "${selectedNode.name}".`, 
                              type: 'success' 
                            });
                            setTimeout(() => setNotification(null), 3000);
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        <Trash2 size={16} />
                        Delete Devices
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <p className="text-sm text-slate-600">
                    Collection contains <span className="font-semibold">{filteredDevices.length}</span> device(s).
                    {selectedNode.isCustom && (
                      <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Custom Collection</span>
                    )}
                  </p>
                  {(() => {
                    const criticalCount = (
                      customCollections.find((c) => c.id === selectedNode.id)?.criticalDeviceIds || []
                    ).length;
                    return criticalCount > 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-100 px-2.5 py-1 rounded-full">
                        <AlertTriangle size={12} />
                        {criticalCount} marked critical → honeycomb cards
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">
                        Select devices and click <strong>Mark Critical</strong> to highlight them on Overview honeycomb cards.
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* --- PLATFORM COMPARISON DASHBOARD --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'cat-platforms' && (
            <PlatformComparisonDashboard devices={devices} />
          )}

          {/* --- APPLICATION DEPLOYMENT --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'application-deployment' && (
            <ApplicationDeployment deviceGroups={deviceGroups} />
          )}

          {/* --- GO AGENT MANAGEMENT --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'go-agent-management' && (
            <GoAgentManagement />
          )}

          {/* --- PATCH MANAGEMENT --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'patch-management' && (
            <PatchManagement />
          )}

          {/* --- LICENSE MANAGEMENT --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'license-management' && (
            <LicenseManagement />
          )}

          {/* --- CLIENT SETTINGS --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'client-settings' && (
            <AgentSettings />
          )}

          {/* --- DEVICE ANALYTICS VIEWS --- */}
          {!isDashboard && !isBaselineView && isAnalyticsView && (
            <>
              {selectedNode.id === 'custom-reports' && (
                <CustomReports />
              )}
              {DEFAULT_ANALYTICS_REPORTS.some((r) => r.id === selectedNode.id) && (() => {
                const report = DEFAULT_ANALYTICS_REPORTS.find((r) => r.id === selectedNode.id);
                return report ? (
                  <DefaultReportView
                    reportId={report.reportId}
                    reportName={report.name}
                    description={report.description}
                  />
                ) : null;
              })()}
              {selectedNode.type === 'analytics-group' && (
                <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center max-w-3xl mx-auto mt-10">
                  <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-6">
                    <FileText size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">{selectedNode.name}</h2>
                  <p className="text-slate-600 mb-6">{selectedNode.description}</p>
                  <p className="text-sm text-slate-500">Select a report from the sidebar to view details</p>
                </div>
              )}
            </>
          )}

          {/* --- COLLECTION MANAGEMENT (Collections) --- */}
          {!isDashboard && !isBaselineView && selectedNode.id === 'cat-depts' && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-300 mb-6">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50/50">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Briefcase size={18} className="text-indigo-500" />
                  Manage Groups
                </h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      const name = window.prompt('Enter folder name:');
                      if (name && name.trim()) {
                        const folderId = `folder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                        const newFolder = {
                          id: folderId,
                          name: name.trim(),
                          createdAt: new Date().toISOString()
                        };
                        const updatedFolders = [...customFolders, newFolder];
                        saveCustomFolders(updatedFolders);
                        setNotification({ 
                          title: 'Folder Created', 
                          msg: `Folder "${name}" has been created.`, 
                          type: 'success' 
                        });
                        setTimeout(() => setNotification(null), 3000);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    <FolderPlus size={16} />
                    Create Folder
                  </button>
                  <button 
                    onClick={() => {
                      const name = window.prompt('Enter collection name:');
                      if (name && name.trim()) {
                        // Show folder selection dropdown
                        let folderId = null;
                        if (customFolders.length > 0) {
                          const folderOptions = ['(None - Root Level)', ...customFolders.map(f => f.name)].join('\n');
                          const folderIndex = window.prompt(
                            `Select folder (enter number):\n\n${customFolders.map((f, i) => `${i + 1}. ${f.name}`).join('\n')}\n0. None (Root Level)`
                          );
                          if (folderIndex !== null) {
                            const index = parseInt(folderIndex);
                            if (index > 0 && index <= customFolders.length) {
                              folderId = customFolders[index - 1].id;
                            }
                          }
                        }
                        
                        const newDeptId = `dept-custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                        const newCollection = {
                          id: newDeptId,
                          name: name.trim(),
                          folderId: folderId,
                          createdAt: new Date().toISOString(),
                          criticalDeviceIds: [],
                        };
                        const updatedCollections = [...customCollections, newCollection];
                        saveCustomCollections(updatedCollections);
                        setNotification({ 
                          title: 'Collection Created', 
                          msg: `Collection "${name}" has been created${folderId ? ' in folder' : ' at root level'}.`, 
                          type: 'success' 
                        });
                        setTimeout(() => setNotification(null), 3000);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <Plus size={16} />
                    Create Collection
                  </button>
                </div>
              </div>
              <div className="p-4">
                {/* Folders Section */}
                {customFolders.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                      <Folder size={16} />
                      Folders
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {customFolders.map(folder => {
                        const folderCollections = deviceGroups[0]?.children?.find(c => c.id === 'cat-depts')?.children?.find(f => f.id === folder.id)?.children || [];
                        return (
                          <div key={folder.id} className="border-2 border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-purple-50/30">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Folder size={18} className="text-purple-600" />
                                <h4 className="font-semibold text-slate-800">{folder.name}</h4>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    const newName = window.prompt('Enter new folder name:', folder.name);
                                    if (newName && newName.trim() && newName !== folder.name) {
                                      const updated = customFolders.map(f => 
                                        f.id === folder.id ? { ...f, name: newName.trim() } : f
                                      );
                                      saveCustomFolders(updated);
                                      setNotification({ 
                                        title: 'Folder Updated', 
                                        msg: `Folder renamed to "${newName}".`, 
                                        type: 'success' 
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit Folder"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete the "${folder.name}" folder? Collections inside will be moved to root.`)) {
                                      const updatedCollections = customCollections.map(c => 
                                        c.folderId === folder.id ? { ...c, folderId: null } : c
                                      );
                                      const updatedFolders = customFolders.filter(f => f.id !== folder.id);
                                      void persistDirectory(updatedFolders, updatedCollections);
                                      
                                      setNotification({ 
                                        title: 'Folder Deleted', 
                                        msg: `Folder "${folder.name}" has been deleted.`, 
                                        type: 'success' 
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Folder"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            <div className="text-sm text-slate-600 mb-2">
                              <span className="font-medium">{folderCollections.length}</span> collections
                            </div>
                            {folderCollections.length > 0 && (
                              <div className="text-xs text-slate-500">
                                {folderCollections.slice(0, 3).map(c => c.name).join(', ')}
                                {folderCollections.length > 3 && ` +${folderCollections.length - 3} more`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Groups Section */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                    <Briefcase size={16} />
                    Groups {customFolders.length > 0 && '(Root Level)'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {deviceGroups[0]?.children?.find(c => c.id === 'cat-depts')?.children
                      ?.filter(item => item.type !== 'folder')
                      ?.map(dept => (
                      <div key={dept.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow bg-white ${dept.isCustom ? 'border-blue-300 border-2' : 'border-slate-200'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Briefcase size={18} className={dept.isCustom ? "text-blue-500" : "text-indigo-500"} />
                            <div>
                              <h4 className="font-semibold text-slate-800">{dept.name}</h4>
                              {dept.isCustom && (
                                <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                  {dept.needsName ? 'Needs Name' : 'Custom'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {dept.isCustom && (
                              <>
                                <button
                                  onClick={() => {
                                    const newName = window.prompt('Enter new name:', dept.name);
                                    if (newName && newName.trim() && newName !== dept.name) {
                                      // Check if collection exists in customCollections
                                      const existingCollection = customCollections.find(c => c.id === dept.id);
                                      let updated;
                                      
                                      if (existingCollection) {
                                        // Update existing collection
                                        updated = customCollections.map(c => 
                                          c.id === dept.id ? { ...c, name: newName.trim() } : c
                                        );
                                      } else {
                                        // Create new collection entry for missing custom collection
                                        const newCollection = {
                                          id: dept.id,
                                          name: newName.trim(),
                                          folderId: null,
                                          createdAt: new Date().toISOString()
                                        };
                                        updated = [...customCollections, newCollection];
                                      }
                                      
                                      saveCustomCollections(updated);
                                      setNotification({ 
                                        title: 'Collection Updated', 
                                        msg: `Collection renamed to "${newName}".`, 
                                        type: 'success' 
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete the "${dept.name}" collection?`)) {
                                      const updated = customCollections.filter(c => c.id !== dept.id);
                                      saveCustomCollections(updated);
                                      setNotification({ 
                                        title: 'Collection Deleted', 
                                        msg: `Collection "${dept.name}" has been deleted.`, 
                                        type: 'success' 
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    // Show available collections to move devices to
                                    const availableCollections = deviceGroups[0]?.children?.find(c => c.id === 'cat-depts')?.children
                                      ?.filter(c => c.id !== dept.id && (c.type === 'dept' || (c.type === 'folder' && c.children)))
                                      ?.flatMap(c => c.type === 'folder' ? c.children || [] : [c]) || [];
                                    
                                    if (availableCollections.length === 0) {
                                      window.alert('No other collections available to move devices to.');
                                      return;
                                    }
                                    
                                    const collectionList = availableCollections.map((c, i) => `${i + 1}. ${c.name}`).join('\n');
                                    const selectedIndex = window.prompt(
                                      `Select target collection (enter number):\n\n${collectionList}`
                                    );
                                    
                                    if (selectedIndex !== null) {
                                      const index = parseInt(selectedIndex) - 1;
                                      if (index >= 0 && index < availableCollections.length) {
                                        const targetCollection = availableCollections[index];
                                        const devicesToMove = devices.filter(d => d.dept === dept.id);
                                        const updatedDevices = devices.map(d => 
                                          d.dept === dept.id ? { ...d, dept: targetCollection.id } : d
                                        );
                                        setDevices(updatedDevices);
                                        setNotification({ 
                                          title: 'Devices Moved', 
                                          msg: `${devicesToMove.length} device(s) moved from "${dept.name}" to "${targetCollection.name}".`, 
                                          type: 'success' 
                                        });
                                        setTimeout(() => setNotification(null), 3000);
                                      }
                                    }
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                  title="Move Devices"
                                >
                                  <Move size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    setDeviceImportModal({
                                      isOpen: true,
                                      collectionId: dept.id,
                                      collectionName: dept.name,
                                      importMode: 'single'
                                    });
                                  }}
                                  className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Add Devices to Collection"
                                >
                                  <Plus size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-slate-600">
                          <span className="font-medium">{devices.filter(d => d.dept === dept.id).length}</span> devices
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* --- DEVICES LIST --- */}
          {/* Explicitly exclude Go Agent Management page from showing devices list */}
          {selectedNode.id !== 'go-agent-management' && selectedNode.id !== 'patch-management' && selectedNode.id !== 'license-management' && selectedNode.id !== 'client-settings' && !isDashboard && !isBaselineView && !isAnalyticsView && viewMode === 'devices' && selectedNode.type !== 'folder' && selectedNode.id !== 'cat-depts' && selectedNode.id !== 'cat-platforms' && selectedNode.id !== 'application-deployment' && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-300">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input type="text" placeholder="Search endpoints..." className="pl-9 pr-4 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none w-64" />
                </div>
                <div className="flex gap-2">
                  <button className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm text-slate-600 bg-white hover:bg-slate-50">
                    <Filter size={14} /> Filters
                  </button>
                  {filteredDevices.length > 0 && (
                    <button
                      onClick={() => {
                        // Export to Excel (CSV)
                        const headers = ['Device ID', 'Name', 'OS', 'Platform', 'Status', 'Last Seen', 'IP Address', 'Department', 'Serial Number', 'Last Logged On User'];
                        let content = headers.join(',') + '\n';
                        filteredDevices.forEach(device => {
                          const metadata = device.device_metadata || {};
                          const serialNumber = metadata.serial_number || metadata.serialNumber || metadata.system_serial || 'N/A';
                          const lastLoggedOnUser = metadata.last_logged_on_user || metadata.current_user || metadata.logged_in_user || metadata.user || 'Unknown';
                          const lastSeen = device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never';
                          const row = [
                            device.id || device.device_id || '',
                            device.name || '',
                            device.os || '',
                            device.platform || '',
                            device.status || '',
                            lastSeen,
                            device.ip || '',
                            device.dept || '',
                            serialNumber,
                            lastLoggedOnUser
                          ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
                          content += row + '\n';
                        });
                        const blob = new Blob([content], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${selectedNode.name || 'All_Devices'}_${new Date().toISOString().split('T')[0]}.csv`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      <FileSpreadsheet size={14} />
                      {t('common.export')} {t('common.toExcel', 'to Excel')}
                    </button>
                  )}
                </div>
              </div>
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-xs">
                  <tr>
                    <th className="px-4 py-3 w-10 text-center"><input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={filteredDevices.length > 0 && selectedCount === filteredDevices.length} onChange={toggleAll} /></th>
                    <th className="px-4 py-3">{t('devices.health', 'Health')}</th>
                    <th className="px-4 py-3">{t('common.status')}</th>
                    <th className="px-4 py-3">{t('devices.deviceName', 'Device Name')}</th>
                    <th className="px-4 py-3">{t('devices.userOwner', 'User/Owner')}</th>
                    <th className="px-4 py-3">{t('devices.lastLoggedOnUser', 'Last Logged On User')}</th>
                    <th className="px-4 py-3">{t('devices.serialNumber', 'Serial Number')}</th>
                    <th className="px-4 py-3">{t('devices.osPlatform', 'OS Platform')}</th>
                    <th className="px-4 py-3">{t('devices.ipAddress', 'IP Address')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDevices.map(device => {
                    const isOnline = isDeviceOnline(device.last_seen);
                    const health = getDeviceHealth(device.last_seen);
                    const deviceKey = device.id || device.device_id;
                    const markedCritical =
                      selectedNode.type === 'dept'
                        ? isDeviceCriticalInCollection(selectedNode.id, deviceKey)
                        : isDeviceCriticalInCollection(device.dept, deviceKey);
                    // Get last logged on user - exclude serial_number and other non-user fields
                    const metadata = device.device_metadata || {};
                    
                    // Get hostname for validation
                    const hostname = metadata.hostname || metadata.Hostname || device.hostname || device.name || '';
                    
                    // Get raw values from metadata
                    let rawSerialNumber = metadata.serial_number || metadata.serialNumber || metadata.system_serial || null;
                    let rawCurrentUser = metadata.current_user || metadata.last_logged_on_user || null;
                    
                    // DETECT DATA SWAP: Check if serial_number and current_user are swapped
                    // Pattern: serial_number contains hostname pattern, current_user looks like serial number
                    const hostnamePatterns = ['DESKTOP-', 'LAPTOP-', 'PC-', 'WIN-', 'COMPUTER-'];
                    const isSerialLikelyHostname = rawSerialNumber && hostnamePatterns.some(pattern => 
                      rawSerialNumber.toUpperCase().startsWith(pattern)
                    );
                    const isUserLikelySerial = rawCurrentUser && /^[A-Z0-9]{6,20}$/.test(rawCurrentUser) && 
                                              !rawCurrentUser.includes('.') && 
                                              !rawCurrentUser.includes('-') && 
                                              !rawCurrentUser.includes('_') &&
                                              rawCurrentUser !== hostname;
                    
                    // If data appears swapped, correct it
                    let serialNumber, lastLoggedOnUser;
                    if (isSerialLikelyHostname && isUserLikelySerial) {
                      // Data is swapped: serial_number has hostname, current_user has serial
                      serialNumber = rawCurrentUser; // Use current_user as serial number
                      lastLoggedOnUser = null; // Don't use serial_number as user
                      console.warn(`Device ${device.id}: Detected swapped metadata - corrected serial_number and current_user`);
                    } else {
                      // Normal case: use values as-is
                      serialNumber = rawSerialNumber;
                      lastLoggedOnUser = rawCurrentUser || metadata.logged_in_user || metadata.user || null;
                      
                      // Additional validation: ensure serial_number is not hostname
                      if (serialNumber && (serialNumber === hostname || hostnamePatterns.some(pattern => 
                        serialNumber.toUpperCase().startsWith(pattern)))) {
                        serialNumber = null; // Invalid serial number
                      }
                      
                      // Additional validation: ensure lastLoggedOnUser is not serial number
                      if (lastLoggedOnUser && /^[A-Z0-9]{6,20}$/.test(lastLoggedOnUser) && 
                          !lastLoggedOnUser.includes('.') && !lastLoggedOnUser.includes('-') && 
                          !lastLoggedOnUser.includes('_') && lastLoggedOnUser !== hostname) {
                        // Looks like serial number, not a user
                        if (lastLoggedOnUser === serialNumber) {
                          lastLoggedOnUser = null;
                        }
                      }
                    }
                    
                    // Final assignments with fallbacks
                    serialNumber = serialNumber || t('common.na', 'N/A');
                    
                    // Try other user fields if current_user was invalid
                    if (!lastLoggedOnUser || lastLoggedOnUser === serialNumber) {
                      lastLoggedOnUser = metadata.last_logged_on_user || 
                                        metadata.logged_in_user || 
                                        metadata.user ||
                                        metadata.username ||
                                        null;
                    }
                    
                    // Final validation: if still matches serial number, set to Unknown
                    if (lastLoggedOnUser && (lastLoggedOnUser === serialNumber || lastLoggedOnUser === t('common.na', 'N/A'))) {
                      lastLoggedOnUser = t('common.unknown');
                    }
                    
                    // Final fallback
                    if (!lastLoggedOnUser) {
                      lastLoggedOnUser = t('common.unknown');
                    }
                    return (
                      <tr key={device.id} className={`transition-colors ${checkedDevices[device.id] ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
                        <td className="px-4 py-3 text-center"><input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={!!checkedDevices[device.id]} onChange={() => toggleDevice(device.id)} /></td>
                        {/* Health: Active / Inactive / Critical based on last_seen + collection critical pin */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${
                              markedCritical || health === 'critical' ? 'bg-red-100 text-red-700' :
                              health === 'active' ? 'bg-green-100 text-green-700' :
                              health === 'inactive' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                markedCritical || health === 'critical' ? 'bg-red-500' :
                                health === 'active' ? 'bg-green-500' :
                                health === 'inactive' ? 'bg-amber-500' :
                                'bg-red-500'
                              }`}></span>
                              {markedCritical ? 'Critical (marked)' : health === 'active' ? 'Active' : health === 'inactive' ? 'Inactive' : 'Critical'}
                            </span>
                          </div>
                        </td>
                        {/* Status: Online / Offline with relative last-seen time */}
                        <td className="px-4 py-3">
                          {isOnline ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Online
                            </span>
                          ) : (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 w-fit">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                                Offline
                              </span>
                              <span className="text-[10px] text-slate-400 mt-0.5 pl-0.5">{formatRelativeTime(device.last_seen)}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-700">{device.name}<div className="text-[10px] text-slate-400 font-normal">{device.type}</div></td>
                        <td className="px-4 py-3 text-slate-600">
                          {(() => {
                            const customCollection = customCollections.find(c => c.id === device.dept);
                            if (customCollection) {
                              return `${customCollection.name} User`;
                            }
                            const deptName = device.dept === 'Unassigned' ? 'Unassigned' : device.dept.replace('dept-', '').split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                            return `${deptName} User`;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{lastLoggedOnUser}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{serialNumber}</td>
                        <td className="px-4 py-3"><div className="flex items-center gap-2">{device.platform.includes('windows') && <i className="fab fa-windows text-blue-500" style={{ fontSize: '14px' }} />}{device.platform.includes('linux') && <i className="fab fa-linux text-orange-500" style={{ fontSize: '14px' }} />}{device.platform.includes('macos') && <i className="fab fa-apple text-slate-600" style={{ fontSize: '14px' }} />}{device.platform.includes('android') && <i className="fab fa-android text-green-500" style={{ fontSize: '14px' }} />}{device.platform.includes('ios') && <i className="fab fa-app-store-ios text-gray-500" style={{ fontSize: '14px' }} />}<span className="text-slate-600">{device.os}</span></div></td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{device.ip}</td>
                        <td className="px-4 py-3 text-right relative action-menu-container">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenu(openActionMenu === device.id ? null : device.id);
                            }}
                            className="text-slate-400 hover:text-blue-600 p-1 rounded hover:bg-slate-100 relative"
                          >
                            <MoreVertical size={16} />
                          </button>
                          {openActionMenu === device.id && (
                            <div className="absolute right-0 top-8 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[150px]">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const collectionId = selectedNode.type === 'dept' ? selectedNode.id : device.dept;
                                  const collectionName =
                                    selectedNode.type === 'dept'
                                      ? selectedNode.name
                                      : customCollections.find((c) => c.id === device.dept)?.name || device.dept;
                                  if (!collectionId || collectionId === 'Unassigned') {
                                    window.alert('Move the device into a collection first, then mark it critical.');
                                    setOpenActionMenu(null);
                                    return;
                                  }
                                  setDevicesCriticalInCollection(
                                    collectionId,
                                    collectionName,
                                    [deviceKey],
                                    !markedCritical,
                                    selectedNode.folderId ?? null
                                  );
                                  setOpenActionMenu(null);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${
                                  markedCritical ? 'text-slate-700' : 'text-rose-700'
                                }`}
                              >
                                <AlertTriangle size={14} />
                                {markedCritical ? 'Unmark Critical' : 'Mark as Critical'}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Check if there are selected devices for bulk move
                                  const selectedDeviceIds = Object.keys(checkedDevices).filter(id => checkedDevices[id]);
                                  if (selectedDeviceIds.length > 0 && selectedDeviceIds.includes(device.id)) {
                                    // Bulk move: include all selected devices
                                    const selectedDevices = devices.filter(d => selectedDeviceIds.includes(d.id));
                                    setMoveModal({ 
                                      isOpen: true, 
                                      deviceIds: selectedDeviceIds, 
                                      deviceNames: selectedDevices.map(d => d.name) 
                                    });
                                  } else {
                                    // Single device move
                                    setMoveModal({ 
                                      isOpen: true, 
                                      deviceIds: [device.id], 
                                      deviceNames: [device.name] 
                                    });
                                  }
                                  setOpenActionMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <Move size={14} />
                                Move
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setOpenActionMenu(null);
                                  setUsersModal({ isOpen: true, deviceId: device.id, deviceName: device.name });
                                  setLoadingUsers(true);
                                  try {
                                    const users = await apiService.getDeviceUsers(device.id);
                                    setDeviceUsers(users);
                                  } catch (error) {
                                    console.error('Failed to fetch device users:', error);
                                    setDeviceUsers([]);
                                  } finally {
                                    setLoadingUsers(false);
                                  }
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <Users size={14} />
                                View Users
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Are you sure you want to delete device "${device.name}"? This action cannot be undone.`)) {
                                    try {
                                      await apiService.deleteGoAgent(device.id);
                                      // Remove device from local state
                                      setDevices(devices.filter(d => d.id !== device.id));
                                      setNotification({
                                        title: 'Device Deleted',
                                        msg: `Device "${device.name}" has been deleted successfully.`,
                                        type: 'success'
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    } catch (error) {
                                      setNotification({
                                        title: 'Delete Failed',
                                        msg: `Failed to delete device: ${error.message}`,
                                        type: 'error'
                                      });
                                      setTimeout(() => setNotification(null), 3000);
                                    }
                                  }
                                  setOpenActionMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredDevices.length === 0 && <tr><td colSpan="9" className="px-6 py-16 text-center text-slate-400">No devices found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* --- SCRIPTS/QUICK ACTIONS --- */}
          {!isAnalyticsView && (isBaselineView || (!isDashboard && viewMode === 'scripts') || viewMode === 'adhoc') && (
            <div className="animate-in slide-in-from-bottom-5 duration-300 h-full">
              {viewMode === 'adhoc' ? (
                <AdHocScriptView devices={devices} deviceGroups={deviceGroups} />
              ) : selectedNode.type === 'baseline-group' ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center max-w-3xl mx-auto mt-10">
                  <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-6">
                    <Layers size={48} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">{selectedNode.name}</h2>
                  <p className="text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">{selectedNode.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                       <div className="text-blue-600 mb-2"><FileText size={20} /></div>
                       <h4 className="font-bold text-slate-800 text-sm">Defined Templates</h4>
                       <p className="text-2xl font-bold text-slate-900 mt-1">{selectedNode.children ? selectedNode.children.length : 0}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                       <div className="text-green-600 mb-2"><CheckSquare size={20} /></div>
                       <h4 className="font-bold text-slate-800 text-sm">Compliance Status</h4>
                       <p className="text-2xl font-bold text-slate-900 mt-1">98%</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                       <div className="text-purple-600 mb-2"><Target size={20} /></div>
                       <h4 className="font-bold text-slate-800 text-sm">Active Assignments</h4>
                       <p className="text-2xl font-bold text-slate-900 mt-1">12</p>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-slate-100 flex justify-center gap-4">
                    <p className="text-sm text-slate-400 flex items-center gap-2">
                      <AlertCircle size={14} /> Select a specific policy template from the sidebar to view and deploy scripts.
                    </p>
                  </div>
                </div>
              ) : selectedNode.id === 'tmpl-policies' ? (
                // Show platform policy list for Policies
                <PolicyDeviceCards
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType)}
                  customPolicies={customPolicies.general || {}}
                />
              ) : selectedNode.id === 'tmpl-patch' ? (
                // Show device type cards for Patch Management
                <BaselineDeviceCards
                  baselineType="patch-management"
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType, 'patch-management')}
                  customPolicies={customPolicies.baselines?.['patch-management'] || {}}
                />
              ) : selectedNode.id === 'tmpl-dev-env' ? (
                // Show device type cards for Developer Env Setup
                <BaselineDeviceCards
                  baselineType="developer-env"
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType, 'developer-env')}
                  customPolicies={customPolicies.baselines?.['developer-env'] || {}}
                />
              ) : selectedNode.id === 'tmpl-server-hard' ? (
                // Show device type cards for Server Hardening
                <BaselineDeviceCards
                  baselineType="server-hardening"
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType, 'server-hardening')}
                  customPolicies={customPolicies.baselines?.['server-hardening'] || {}}
                />
              ) : selectedNode.id === 'tmpl-cis-lvl1' ? (
                // Show device type cards for CIS Level 1 Benchmark
                <BaselineDeviceCards
                  baselineType="cis-benchmark"
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType, 'cis-benchmark')}
                  customPolicies={customPolicies.baselines?.['cis-benchmark'] || {}}
                />
              ) : selectedNode.id === 'tmpl-audit' ? (
                // Show device type cards for Full System Audit
                <BaselineDeviceCards
                  baselineType="system-audit"
                  scripts={scriptLibrary}
                  onSelectScript={handleDeployClick}
                  onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType, 'system-audit')}
                  customPolicies={customPolicies.baselines?.['system-audit'] || {}}
                />
              ) : (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-slate-700">{isBaselineView ? 'Baseline Templates' : 'Quick Actions'}</h2>
                    {!isBaselineView && selectedCount > 0 && <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">Targeting {selectedCount} Selected Device(s)</div>}
                    {isBaselineView && <button onClick={() => setViewMode('adhoc')} className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1"><Zap size={14} /> Run Ad-hoc Script</button>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {quickActionScripts.map(script => (
                      <div key={script.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all group relative">
                        <div className="flex justify-between items-start mb-4">
                          <div className={`p-3 rounded-lg bg-slate-50 ${script.color} group-hover:bg-opacity-10 transition-colors`}><script.icon size={24} /></div>
                          <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200">{script.lang}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">{script.title}</h3>
                        <p className="text-sm text-slate-500 mb-6 h-10 line-clamp-2">{script.description}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${script.risk === 'High' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>{script.risk} Risk</span>
                          <button onClick={() => handleDeployClick(script)} className="flex items-center gap-2 bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-600 hover:shadow-md transition-all active:scale-95"><Play size={14} /> {isBaselineView ? 'Select Targets' : 'Run'}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>

      <DeploymentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} script={selectedScript} preSelectedTarget={!isBaselineView ? { name: selectedCount > 0 ? `${selectedCount} Devices` : selectedNode.name, count: selectedCount || filteredDevices.length } : null} onConfirm={confirmDeployment} deviceGroups={deviceGroups} devices={devices} />
      
      <DeviceImportModal
        isOpen={deviceImportModal.isOpen}
        onClose={() => setDeviceImportModal({ ...deviceImportModal, isOpen: false })}
        collectionId={deviceImportModal.collectionId}
        collectionName={deviceImportModal.collectionName}
        devices={devices}
        onAddDevices={(deviceIds, collectionId) => {
          const updatedDevices = devices.map(d => {
            // Check if device ID matches any of the provided IDs
            const matches = deviceIds.some(id => 
              d.id === id || 
              d.device_id === id || 
              d.name === id ||
              d.id?.includes(id) ||
              d.device_id?.includes(id) ||
              d.name?.includes(id)
            );
            if (matches && d.dept !== collectionId) {
              return { ...d, dept: collectionId };
            }
            return d;
          });
          setDevices(updatedDevices);
          setNotification({ 
            title: 'Devices Added', 
            msg: `${deviceIds.length} device(s) added to "${deviceImportModal.collectionName}".`, 
            type: 'success' 
          });
          setTimeout(() => setNotification(null), 3000);
        }}
      />

      <PolicyBuilderModal
        isOpen={policyModal.isOpen}
        deviceType={policyModal.deviceType}
        baselineType={policyModal.baselineType}
        onClose={closePolicyBuilder}
        onSave={handlePolicySave}
      />

      {/* Move Device Modal */}
      {/* Device Users Modal */}
      {usersModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setUsersModal({ isOpen: false, deviceId: null, deviceName: null })}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Users on {usersModal.deviceName}</h3>
                <p className="text-sm text-slate-500 mt-1">List of all logged-in users for this device</p>
              </div>
              <button onClick={() => setUsersModal({ isOpen: false, deviceId: null, deviceName: null })} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-slate-500">Loading users...</div>
                </div>
              ) : deviceUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 font-medium">No users found</p>
                  <p className="text-sm text-slate-400 mt-2">This device hasn't reported any logged-in users yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Users will appear here once your agent sends user data to the server.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Username</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Full Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Login</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {deviceUsers.map((user, index) => (
                      <tr key={user.user_id || index} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">{user.username || t('common.na', 'N/A')}</td>
                        <td className="px-4 py-3 text-slate-600">{user.full_name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{user.email || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            user.user_type === 'domain' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {user.user_type || 'local'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {user.is_primary_user && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                Primary
                              </span>
                            )}
                            {user.is_admin && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                Admin
                              </span>
                            )}
                            {!user.is_primary_user && !user.is_admin && (
                              <span className="text-xs text-slate-400">Standard</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : t('common.never', 'Never')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setUsersModal({ isOpen: false, deviceId: null, deviceName: null })}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {moveModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setMoveModal({ isOpen: false, deviceIds: [], deviceNames: [] })}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">
                {moveModal.deviceIds.length > 1 ? 'Move Devices' : 'Move Device'}
              </h3>
              <button onClick={() => setMoveModal({ isOpen: false, deviceIds: [], deviceNames: [] })} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {moveModal.deviceIds.length > 1 ? (
                <>Move <span className="font-semibold">{moveModal.deviceIds.length} selected devices</span> to a different collection:</>
              ) : (
                <>Move <span className="font-semibold">{moveModal.deviceNames[0]}</span> to a different collection:</>
              )}
            </p>
            <select
              className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              onChange={async (e) => {
                const targetCollectionId = e.target.value;
                if (targetCollectionId) {
                  const targetCollection = deviceGroups[0]?.children?.find(c => c.id === 'cat-depts')?.children
                    ?.flatMap(c => c.type === 'folder' ? (c.children || []) : [c])
                    ?.find(c => c.id === targetCollectionId);
                  
                  // Update all selected devices
                  let successCount = 0;
                  let failCount = 0;
                  
                  for (const deviceId of moveModal.deviceIds) {
                    try {
                      await apiService.updateDeviceDepartment(deviceId, targetCollectionId);
                      successCount++;
                    } catch (error) {
                      console.error(`Failed to move device ${deviceId}:`, error);
                      failCount++;
                    }
                  }
                  
                  // Update local state
                  const updatedDevices = devices.map(d => 
                    moveModal.deviceIds.includes(d.id) ? { ...d, dept: targetCollectionId } : d
                  );
                  setDevices(updatedDevices);
                  
                  // Clear selections
                  setCheckedDevices({});
                  
                  // Show notification
                  if (failCount === 0) {
                    setNotification({
                      title: 'Devices Moved',
                      msg: `${successCount} device(s) moved to "${targetCollection?.name || targetCollectionId}".`,
                      type: 'success'
                    });
                  } else {
                    setNotification({
                      title: 'Move Partially Completed',
                      msg: `${successCount} device(s) moved successfully, ${failCount} failed.`,
                      type: 'error'
                    });
                  }
                  setTimeout(() => setNotification(null), 3000);
                  setMoveModal({ isOpen: false, deviceIds: [], deviceNames: [] });
                }
              }}
              defaultValue=""
            >
              <option value="">-- Select Collection --</option>
              {deviceGroups[0]?.children?.find(c => c.id === 'cat-depts')?.children
                ?.flatMap(c => c.type === 'folder' ? (c.children || []) : [c])
                ?.filter(c => {
                  if (c.type !== 'dept') return false;
                  // Filter out collections that all selected devices are already in
                  const currentDepts = moveModal.deviceIds.map(id => devices.find(d => d.id === id)?.dept).filter(Boolean);
                  return !currentDepts.every(dept => dept === c.id);
                })
                ?.map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={() => setMoveModal({ isOpen: false, deviceIds: [], deviceNames: [] })}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-600 font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {settingsModalOpen && (
        <Settings onClose={() => setSettingsModalOpen(false)} />
      )}
      
      {notification && (
        <div className={`fixed bottom-6 right-6 bg-slate-800 text-white shadow-2xl rounded-lg p-4 flex items-center gap-3 animate-in slide-in-from-bottom-5 duration-300 z-50`}>
          <div className={`p-1 rounded-full text-white ${notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
            {notification.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          </div>
          <div>
            <h4 className="font-bold text-sm">{notification.title}</h4>
            <p className="text-xs text-slate-300">{notification.msg}</p>
          </div>
        </div>
      )}
    </div>
  );
}
